import React, { useState, useEffect, useRef, useMemo, useCallback, useReducer } from 'react';
import axiosInstance from '../services/axiosInstance';
import GlobalTable from '../components/GlobalTable';
import { useLazyTable } from '../hooks/useLazyTable';
import CustomerDialog from '../components/CreateCustomerDialog';
import PrescriptionDialog from '../components/CreatePrescriptionDialog';
import InvoicePrintView from '../components/InvoicePrintView';
import { validatePhoneNumber } from '../components/utils';
import { format } from 'date-fns';
import { AutoComplete } from 'primereact/autocomplete';
import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { handleApiError, handleApiSuccess } from '../components/apiErrorHandler';
import { InputNumber } from 'primereact/inputnumber';
import { Dropdown } from 'primereact/dropdown';
import { FaLock, FaUnlock, FaFlag, FaInfoCircle } from "react-icons/fa";  
import { Password } from 'primereact/password';
import { InputTextarea } from 'primereact/inputtextarea';
import { confirmDialog } from 'primereact/confirmdialog';
import { useLocation, useSearchParams, useNavigate } from "react-router-dom";
import { FilterMatchMode } from 'primereact/api';
import { BatchAllocationDialog } from '../components/BatchAllocationDialog';
import InlineBatchEditorDialog from '../components/InlineBatchEditorDialog';
import { ProductAutoCompleteCell } from '../components/ProductSelector';
import { allocateBatchesForItems } from '../services/batchAllocator';
import { useBreadcrumbs } from '../contexts/BreadcrumbContext';
import { printInvoice } from '../printing/PrinterService';
import PrinterModeConfirmDialog from '../printing/PrinterModeConfirmDialog';
import OperatorAuthDialog from '../components/OperatorAuthDialog';
import UploadFilePanel from '../components/UploadFilePanel';
import CreateInvoiceDialog from '../components/CreateInvoiceDialog';
import { usePermission } from '../contexts/UserContext';
import PaymentDialog from '../components/PaymentDialog';
import ExceptionalInventoryDialog from '../components/ExceptionalInventoryDialog';

// ── LiveClock — isolated so its 1s interval only re-renders itself ──
const LiveClock = () => {
    const [now, setNow] = useState(new Date());
    useEffect(() => {
        const id = setInterval(() => setNow(new Date()), 1000);
        return () => clearInterval(id);
    }, []);
    return now;
};

// ── Static lookup data (defined outside component — never recreated on render) ──
const FREQUENCY_OPTIONS = [
    {label: "BD", value: "BD"},
    {label: "TDS", value: "TDS"},
    {label: "QDS", value: "QDS"},
    {label: "Mane", value: "Mane"},
    {label: "Nocte", value: "Nocte"},
    {label: "QHS", value: "QHS"},
    {label: "Q4H", value: "Q4H"},
    {label: "Q6H", value: "Q6H"},
    {label: "Q8H", value: "Q8H"},
    {label: "Q12H", value: "Q12H"},
    {label: "EOD", value: "EOD"},
    {label: "STAT", value: "STAT"},
    {label: "SOS", value: "SOS"},
    {label: "PRN", value: "PRN"},
    {label: "Other", value: "Other"},
];
const TITLE_OPTIONS = [
    { label: "Mr.", value: "Mr." },
    { label: "Mss.", value: "Mss." },
    { label: "Mrs.", value: "Mrs." },
    { label: "Dr.", value: "Dr." },
    { label: "Ven.", value: "Ven." },
    { label: "Rev.", value: "Rev." },
];
const SEGMENT_OPTIONS = [
    { label: "High Revenue", value: "High Revenue" },
    { label: "Medium Revenue", value: "Medium Revenue" },
    { label: "Low Revenue", value: "Low Revenue" },
    { label: "Negative Revenue", value: "Negative Revenue" },
];
const CATEGORY_OPTIONS = [
    {label: "Store visitors", value: "Store visitors" },
    {label: "Home delivery", value: "Home delivery" },
    {label: "Convenience", value: "Convenience" },
    {label: "Loyal", value: "Loyal" },
];
const PRIORITY_OPTIONS = [
    {label: 1, value: 1 },
    {label: 2, value: 2 },
    {label: 3, value: 3 },
    {label: 4, value: 4 },
    {label: 5, value: 5 },
];
const STATUS_OPTIONS = [
    {label: "Regular", value: "Regular" },
    {label: "Blacklisted", value: "Blacklisted" },
    {label: "Inactive", value: "Inactive" },
];
const FREQUENCY_MAP = {
    BD: 2, TDS: 3, QDS: 4, Mane: 1, Nocte: 1,
    QHS: 1, Q4H: 6, Q6H: 4, Q8H: 3, Q12H: 2, EOD: .5,
};

const InvoiceList = () => {
    const [invoices, setInvoices] = useState([]);
    const [filteredCustomers, setFilteredCustomers] = useState([]);
    const [dialogName, setDialogName] = useState('Create Invoice');
    const location = useLocation();
    const navigate = useNavigate();
    const { invId, customerId, fromUploadId } = location.state || {};
    // Ref for portal upload ID — updated whenever location.state changes
    // so it works both on fresh mount AND when navigated to from an already-mounted InvoiceList
    const portalPrescriptionUploadId = useRef(location.state?.portalPrescriptionUploadId || null);
    useEffect(() => {
        if (location.state?.portalPrescriptionUploadId) {
            portalPrescriptionUploadId.current = location.state.portalPrescriptionUploadId;
        }
    }, [location.state?.portalPrescriptionUploadId]);
    // Upload panel for existing invoices that have a linked portal upload
    const [invoiceUploadData, setInvoiceUploadData] = useState(null);
    const [showCreditNoteDialog, setshowCreditNoteDialog] = useState(false);
    const [selectedItem, setSelectedItem] = useState(null); 
    const [creditNoteReason, setCreditNoteReason] = useState("");
    const [isCreditNote, setIsCreditNote] = useState(null);
    const [inventoryInfo, setInventoryInfo] = useState({}); 
    const [inventoryError, setInventoryError] = useState({});
    const { push, pop, reset, beginDrilldown } = useBreadcrumbs();
    const makeBlankItem = (duration = null) => ({
        id: Date.now() + Math.random(),
        product: null,
        quantity: '',
        price: '',
        dosage: '',
        frequency: '',
        duration
    });
    const [invoice, setInvoice] = useState({
        customer: null,
        invoiced_customer: '',
        date: new Date(),
        prescription: null,
        remarks: '',
        total_price: 0,
        discount: null,
        tax_amount: 0,
        net_price: 0, 
        isdiscount_per: true,
        items: [makeBlankItem(null)],
    });
    const [dialogVisible, setDialogVisible] = useState(false);
    const [markReadyLoading, setMarkReadyLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [filteredPrescriptions, setFilteredPrescriptions] = useState([]);
    const [isCustomerDialogVisible, setCustomerDialogVisible] = useState(false);
    const [overrideApproval, setoverrideApproval] = useState();
    const [discountApproval, setdiscountApproval] = useState(); 
    const [passwordDialogVisible, setPasswordDialogVisible] = useState(false);
    const [prescriptionDialogVisible, setPrescriptionDialogVisible] = useState(false);
    const [password, setPassword] = useState("");
    const [passwordPurpose, setPasswordPurpose] = useState(null);
    const [passwordDialogHeader, setPasswordDialogHeader] = useState(null);
    const [priceEditable, setPriceEditable] = useState(false); 
    const [productIssueDialog, setProductIssueDialog] = useState(false);
    const [discountEditable, setDiscountEditable] = useState(false); 
    const [isCompleting, setIsCompleting] = useState(false);
    const [issueDescription, setIssueDescription] = useState("");
    const [confirmNavActive, setConfirmNavActive] = useState(false);
    const frequency = FREQUENCY_OPTIONS;
    const [customer, setCustomer] = useState({
        title: "",
        gender: "",
        first_name: "",
        last_name: "",
        email: null,
        phone_number: null,
        address: "",
        nic: "",
        passport: "",
        dob: null,
        category: "",
        segment: "",
        loyalty: "",
        priority: 5,
        status: "Regular",
        organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
        relationship: "",
        relative: null,
        relative_flg: false,
        loyalty_discount_percent: 0,
    });
    const [prescription, setPrescription] = useState({
            customer: null,
            doctor_name: '',
            date: new Date(),
            items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
        });
    const titles = TITLE_OPTIONS;
    const segments = SEGMENT_OPTIONS;
    const categories = CATEGORY_OPTIONS;
    const priority = PRIORITY_OPTIONS;
    const status = STATUS_OPTIONS;
    const isAdminUser = useMemo(() => {
        const userSettings = JSON.parse(localStorage.getItem('userSettings') || '{}');
        return userSettings?.isAdmin === true;
    }, []);
    const [printerModeDialog, setPrinterModeDialog] = useState({
        visible: false,
        printerName: '',
        requiredLanguage: null,
        onConfirm: null,
    });
    const [searchParams] = useSearchParams();
    const invoiceId = searchParams.get("invoiceId"); // Get invoice ID from URL
    const cusinputRef = useRef(null);
    const prodInputRefsInv = useRef([]);
    const prodInputRefs= useRef([]);
    const [rowBumps, bumpRow] = useReducer(
      (state, id) => ({ ...state, [id]: (state[id] || 0) + 1 }),
      {}
    );
    const choiceAskNextRef = useRef(null);  
    const pendingChoiceQueueRef = useRef([]);
    const pendingNoStockWithoutActiveRef = useRef([]);
    const invoiceFormRef = useRef(null);
    const isCompletingRef = useRef(false);
    const [invoiceToPrint, setInvoiceToPrint] = useState(null);
    const [showPrint, setShowPrint] = useState(false);
    const [batchDialogVisible, setBatchDialogVisible] = useState(false);
    const [batchAllocations, setBatchAllocations] = useState([]);
    const [pendingItemId, setPendingItemId] = useState(null);
    const [pendingProduct, setPendingProduct] = useState(null);
    const [pendingQty, setPendingQty] = useState(0);
    const [pendingExceptional, setPendingExceptional] = useState(null);
    const [batchAllocDialogVisible, setBatchAllocDialogVisible] = useState(false);
    const [batchAllocItems, setBatchAllocItems] = useState([]); 
    const postAllocCallbackRef = useRef(null);
    const [availableItems, setAvailableItems] = useState([]);
    const [excludedBatchesMap, setExcludedBatchesMap] = useState({});  
    const [excludedDialogVisible, setExcludedDialogVisible] = useState(false);
    const [currentExcludedList, setCurrentExcludedList] = useState([]);
    const [showPaymentDialog, setShowPaymentDialog] = useState(false);
    const [manualBatchMap, setManualBatchMap] = useState({}); 
    const [batchOptionsMap, setBatchOptionsMap] = useState({});
    const [manualBatchEnabledFlag, setManualBatchEnabledFlag] = useState({});
    const [scannedQtyDialogVisible, setScannedQtyDialogVisible] = useState(false);
    const [scannedAvailableQty, setScannedAvailableQty] = useState(0);
    const [scannedNewRow, setScannedNewRow] = useState(null);
    const [scannedQtyValue, setScannedQtyValue] = useState(null); 
    // Exceptional (invoice-time) inventory flow
    const [exceptionalVisible, setExceptionalVisible] = useState(false);
    const [exceptionalActiveInfo, setExceptionalActiveInfo] = useState(null);
    const [rxExceptionalQueue, setRxExceptionalQueue] = useState([]);
    const [allowExceptional, setAllowExceptional] = useState(true);
    // ---- Exceptional dialog ref (imperative handle) ----
    const exceptionalDialogRef = useRef(null);
    const savedQtyRef   = useRef({});  
    const inFlightAllocRef = useRef({});
    const resumeAfterExRef = useRef(false);
    const suppressExceptionalAfterSplitRef = useRef(false);
    const tempExceptionalIdsRef = useRef(new Set());
    const scanInputRef = useRef(null);    
    const scannedQtyRef = useRef(null); 
    const [operatorAuthEnabled, setOperatorAuthEnabled] = useState(false);
    const [showOperatorAuth, setShowOperatorAuth] = useState(false);
    const [invoiceOperator, setInvoiceOperator] = useState(null);
    const [operatorAuthKey, setOperatorAuthKey] = useState(0);
    const isNewInvoice = !invoice?.id;
    const [printing, setPrinting] = useState(false);
    const money = (v) => Number(Number(v || 0).toFixed(2));
    const lineTotal = (qty, price) => money(money(qty) * money(price));
    const balanceInvoiceAmount = (invoice?.net_price || 0) - (invoice?.paid_amount || 0);
    const canCreateInvoices   = usePermission('create_invoices');
    const canManageCustomers  = usePermission('manage_customers');
    const canManagePrescriptions = usePermission('manage_prescriptions');
    const canManageCreditNotes = usePermission('manage_credit_notes');
    const canViewPayments     = usePermission('view_payments');
    const { lazyParams, onPage, onSort, onFilter, queryParams } = useLazyTable({ rows: 15 });
    const [totalRecords, setTotalRecords] = useState(0);
    const initCommittedMaps = (inv) => {
    const s = {};
    (inv?.items || []).forEach(it => { s[it.id] = Number(it.quantity || 0); });
    savedQtyRef.current = s;
    };

    const confirmWithNav = (opts) => {
        confirmDialog({
            ...opts,
            onShow: () => setConfirmNavActive(true),
            onHide: () => setConfirmNavActive(false),
        });
    };

    const handleMarkReady = async () => {
        setMarkReadyLoading(true);
        try {
            const res = await axiosInstance.post(`/api/invoices/${invoice.id}/mark-ready/`);
            setInvoice(prev => ({
                ...prev,
                portal_notified_at: res.data.portal_notified_at,
                sms_notified_at: res.data.sms_notified_at,
            }));
            if (invoice.portal_refill_request) {
                await axiosInstance.patch(`/api/refill-requests/${invoice.portal_refill_request}/`, { status: 'processed' });
            } else if (invoice.portal_prescription_upload) {
                await axiosInstance.patch(`/api/prescription-uploads/${invoice.portal_prescription_upload.id}/`, { status: 'processed' });
            }
            handleApiSuccess(
                res.data.sms_sent
                    ? 'Customer notified — portal + SMS sent.'
                    : 'Customer notified.'
            );
        } catch (err) {
            handleApiError(err);
        } finally {
            setMarkReadyLoading(false);
        }
    };

    const orgSettings = useMemo(
        () => JSON.parse(localStorage.getItem('organizationSettings') || '{}'),
        []
    );
    const smsInvoiceEnabled = !!orgSettings?.sms_notify_invoice_ready;
    
    const showMarkReady = !!(
        (invoice.customer_portal_linked &&
            (invoice.portal_refill_request || invoice.portal_prescription_upload)) ||
        (smsInvoiceEnabled && invoice.customer?.phone_number)
    );
    
    // Disable once either notification has been sent
    const markReadyAlreadySent = !!(invoice.portal_notified_at || invoice.sms_notified_at);

    // 🔹 Build payload identical to handleSubmit
    const buildInvoicePayload = () => ({
        ...invoice,
        status: 'Open',
        is_paid: false,
        customer: invoice.customer ? invoice.customer.id : null,
        prescription: invoice.prescription ? invoice.prescription.id : null,
        date: new Date().toISOString(),
        operator_id: invoiceOperator?.operator_id || null,
        operator_verification_method: invoiceOperator?.verification_method || null,
        portal_prescription_upload_id: portalPrescriptionUploadId.current,
        items: invoice.items.map((item) => ({
            ...item,
            product: item.product.id,
            price: Number(item.price).toFixed(2),
            total: Number((item.quantity * item.price).toFixed(2)),
            batch_number: item.batch_number,
            expiry_date: item.expiry_date,
            price_list: item.price_list,
            inventory: item.inventory,
        })),
    });

    const handlePrint = async () => {
        setPrinting(true);
        try {
            let inv = invoice;

            // 🔹 If invoice not saved yet → save first
            if (!inv?.id) {
                if (blockIfUnsavable()) return;
                const payload = buildInvoicePayload();
                const res = await axiosInstance.post(`/api/invoices/`, payload);
                inv = res.data;

                const cleaned = {
                    ...inv,
                    prescription: inv.prescription?.id ?? null,
                    customer_name: inv.customer?.displayLabel ?? null,
                };

                setInvoice(inv);
                setInvoices(prev => [cleaned, ...prev]);
            }

            // 🔹 Now print
            await printInvoice({
                invoice: inv,
                org: {
                    organization_name: inv.organization_name,
                    address: inv.organization_address,
                    contact_number: inv.organization_contact,
                },
                onRequireModeConfirm: (confirmFn) => {
                    setPrinterModeDialog({
                        visible: true,
                        printerName: 'Configured Printer',
                        requiredLanguage: 'ESC_POS',
                        onConfirm: async () => {
                            confirmFn();
                            setPrinterModeDialog({ visible: false });
                        },
                    });
                },
            });
        } catch (e) {
            handleApiError(e);
        } finally { setPrinting(false); }
    };

    useEffect(() => { isCompletingRef.current = isCompleting; }, [isCompleting]);

    useEffect(() => {
    const handleEnterKey = (e) => {
        if (e.key !== "Enter") return;

        const target = e.target;
        const tag = target.tagName;
        const isInput = ["INPUT", "TEXTAREA", "SELECT"].includes(tag);
        const isDisabled = target.disabled || target.readOnly || target.type === "hidden";

        if (!isInput || isDisabled) return;

        e.preventDefault();

        // Handle special case: duration field → jump to product
        if (target.placeholder === "Set duration in days") {
        if (invoice.items.length > 0) {
                    prodInputRefsInv.current[invoice.items[0].id]?.focus();
                }
        return;
        }

        // Handle special case: quantity field in DataTable → add row
        if (target.placeholder === "Duration") {
        addNewItem();
        return;
        }

        // General tab-like behavior
        const form = target.form;
        if (!form) return;

        const focusable = Array.from(
        form.querySelectorAll(
            'input:not([disabled]):not([readonly]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"]):not([disabled])'
        )
        ).filter(el => el.offsetParent !== null); // only visible elements

        const index = focusable.indexOf(target);
        if (index > -1 && index < focusable.length - 1) {
        focusable[index + 1].focus();
        }
    };

    if (dialogVisible) {
        document.addEventListener("keydown", handleEnterKey);
    }

    return () => {
        document.removeEventListener("keydown", handleEnterKey);
    };
    }, [dialogVisible, invoice.items]);

    useEffect(() => {
        if (!resumeAfterExRef.current) return;
        if (rxExceptionalQueue.length === 0 && choiceAskNextRef.current) {
            resumeAfterExRef.current = false;
            const rest = pendingChoiceQueueRef.current || [];
            pendingChoiceQueueRef.current = [];
            setTimeout(() => choiceAskNextRef.current?.(rest), 0);
        }
    }, [rxExceptionalQueue.length]);

    useEffect(() => {
        const onKey = (e) => {
            if (!confirmNavActive) return;

            // Find the currently open PrimeReact ConfirmDialog
            const dlg = document.querySelector('.p-confirm-dialog');
            if (!dlg) return;
            const footer = dlg.querySelector('.p-dialog-footer');
            if (!footer) return;
            const buttons = Array.from(footer.querySelectorAll('button'));
            if (buttons.length === 0) return;

            const focusedIdx = buttons.findIndex((b) => b === document.activeElement);

            if (e.key === 'ArrowRight' || e.key === 'ArrowLeft') {
                e.preventDefault();
                if (focusedIdx === -1) {
                    buttons[0].focus(); // start on first
                    return;
            }
                const delta = e.key === 'ArrowRight' ? 1 : -1;
                const next = (focusedIdx + delta + buttons.length) % buttons.length;
                buttons[next].focus();
            } else if (e.key === 'Enter' && focusedIdx >= 0) {
                e.preventDefault();
                buttons[focusedIdx].click();
            }
        };

        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [confirmNavActive]);

    // Ctrl+P / Cmd+P — same action as the Print button, while the invoice dialog is open.
    useEffect(() => {
        const onPrintShortcut = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'p') {
                e.preventDefault(); // block the browser's own print dialog
                if (dialogVisible && !printing) {
                    handlePrint();
                }
            }
        };

        document.addEventListener('keydown', onPrintShortcut);
        return () => document.removeEventListener('keydown', onPrintShortcut);
    }, [dialogVisible, printing, handlePrint]);

    useEffect(() => {
        if (dialogVisible) {
        setTimeout(() => cusinputRef.current?.focus(), 1000); // Ensures it runs after rendering
        }
    }, [dialogVisible]);
    
    useEffect(() => {
        if (
            dialogVisible &&
            operatorAuthEnabled &&
            isNewInvoice &&
            !invoiceOperator
        ) {
            setOperatorAuthKey(k => k + 1);
            setInvoiceOperator(null);
            setShowOperatorAuth(true);
        }
    }, [dialogVisible, operatorAuthEnabled, isNewInvoice]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!dialogVisible || isReadOnly) return;

            if (event.ctrlKey && (event.key === "a" || event.key === "A")) {
                event.preventDefault();
                if (invoice.items.length > 0) {
                    prodInputRefsInv.current[invoice.items[0].id]?.focus();
                }
            } 
        };
        document.addEventListener("keydown", handleKeyDown);
        return () => document.removeEventListener("keydown", handleKeyDown);
      }, [dialogVisible, invoice.items.length]);

    useEffect(() => {
        const onShortcut = (e) => {
            if (!dialogVisible || isReadOnly) return;
            const save = (e.key === 's' || e.key === 'S') && (e.ctrlKey || e.metaKey);
            const complete = (e.key === 'd' || e.key === 'D') && (e.ctrlKey || e.metaKey);
            if (!save && !complete) return;

            e.preventDefault();

            isCompletingRef.current = !!complete;
            setIsCompleting(!!complete);

            invoiceFormRef.current?.requestSubmit();
        };

        window.addEventListener('keydown', onShortcut);
        return () => window.removeEventListener('keydown', onShortcut);
    }, [dialogVisible]);

    useEffect(() => {
        if (!dialogVisible || isReadOnly) return;

        const onBacktick = (e) => {
            // backtick key is ` (backquote) — key can be '`' or key === '`'
            if (e.key === '`') {
            e.preventDefault();
            scanInputRef.current?.focus();
            // select contents for convenience
            try { scanInputRef.current.select(); } catch (_) {}
            }
        };

        document.addEventListener('keydown', onBacktick);
        return () => document.removeEventListener('keydown', onBacktick);
    }, [dialogVisible]);

    useEffect(() => {
        if (invoiceId) {
            const fetchData = async () => {
                try {
                    const response = await axiosInstance.get(`/api/invoices/${invoiceId}/`);
                    const detailedinvoice = response.data;
                    setInvoice(detailedinvoice);
                    hydrateOperatorFromInvoice(detailedinvoice);
                    initCommittedMaps(detailedinvoice);
                    setDialogVisible(true); // Open dialog automatically
                } catch (error) {
                    handleApiError(error);
                }
            };
            fetchData();
        }
    }, [invoiceId]);

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                if (customerId) {
                    await fetchInvoices(customerId); // Fetch invoices for this customer
                } else {
                    const res = await axiosInstance.get('/api/invoices/', {
                        params: queryParams,
                    });
                    setInvoices(res.data.results);
                    setTotalRecords(res.data.count);
                }
            } catch (error) {
                handleApiError(error);
            } finally {
                setLoading(false);
            }
        };
    
        setOrgSettings(); // Ensure settings are always loaded
        fetchData();
    }, [customerId, queryParams]); 

    useEffect(() => {
        calculatePrices();
    }, [invoice.items]);

    useEffect(() => {
        if (location.state?.customer) {
            const cust = location.state.customer;
            const displayName = cust.displayLabel
                || cust.name
                || `${cust.title||''} ${cust.first_name||''} ${cust.last_name||''}`.trim();
            const custWithLabel = { ...cust, displayLabel: displayName };
            setInvoice((prev) => ({
                ...prev,
                customer: custWithLabel,
                invoiced_customer: displayName,
            }));
            handleSearchPrescriptions(cust.id);
            applyCustomerLoyaltyDiscount(cust);
            setDialogVisible(true);
        }
        else if (location.state?.openCreate) {
            setDialogVisible(true); 
        }
    }, [location.state?.customer, location.state?.openCreate, location.key]);

    useEffect(() => {
        const loadInvoiceFromNav = async () => {
            if (!location.state?.invId) return;

            try {
                const res = await axiosInstance.get(
                    `/api/invoices/${location.state.invId}/`
                );
                setInvoice(res.data);
                hydrateOperatorFromInvoice(res.data);
                setIsReadOnly(res.data.status !== 'Open');
                initCommittedMaps(res.data);
                setFilteredPrescriptions((prev) => {
                    const currentPrescription = res.data.prescription ? [res.data.prescription] : [];
                    return currentPrescription;
                });
                setDialogVisible(true);
                setDialogName('Invoice Details');
            } catch (err) {
                handleApiError(err);
            }
        };

        loadInvoiceFromNav();
    }, [location.state?.invId]);

    useEffect(() => {
        if (isReadOnly) return;
        if (!scannedQtyDialogVisible) {
        // wait for the inputs to mount
            setTimeout(() => {
                scanInputRef.current?.focus?.();
            }, 500);
        }
    }, [scannedQtyDialogVisible]);

    const showExcludedDialog = itemId => {
        setCurrentExcludedList(excludedBatchesMap[itemId] || []);
        setExcludedDialogVisible(true);
    };

    const openPaymentDialog = () => {
        setShowPaymentDialog(true);
    };

    const hydrateOperatorFromInvoice = (inv) => {
        if (inv?.operator) {
            setInvoiceOperator({
                operator_id: inv.operator,
                operator_name: inv.operator_name,
                verification_method: inv.operator_verification_method,
                verified_at: inv.operator_verified_at,
            });
        } else {
            setInvoiceOperator(null);
        }
    };

    const handleScannedCode = async (scanned) => {
        const code = String(scanned || '').trim();
        if (!code) return;
        const batchNumber = code;

        try {
            // ask backend for batch details (adjust endpoint if different)
            const res = await axiosInstance.post('/api/inventory/get-batch-details/', { batch_number: batchNumber });
            const data = res.data || {};

            if (!data || !data.product || !data.batch_number) {
                handleApiError({ Error: 'Batch not found or barcode invalid.' });
                setTimeout(() => {
                    scanInputRef.current?.focus?.();
                }, 500);                                                                
                return;
            }

            const productObj = data.product;
            const newRow = {
                id: Date.now() + Math.random(),
                product: productObj,
                quantity: '', // user will type quantity
                price: data.selling_price ?? (productObj?.default_selling_price ?? ''),
                batch_number: data.batch_number,
                expiry_date: data.expiry_date || null,
                inventory: data.inventory_id || null,
                price_list: data.price_list || null,
                total: 0,
            };

            // prevent duplicate product+batch
            const duplicate = invoice.items.find(i =>
                    i.product?.id === productObj.id &&
                    String(i.batch_number || '').toUpperCase() === String(data.batch_number).toUpperCase()
                );
            if (duplicate) {
                // focus existing row quantity
                bumpRow(duplicate.id);
                handleApiSuccess('Batch already present on invoice — update the quantity.');
                setTimeout(() => {
                    scanInputRef.current?.focus?.();
                }, 500);  
                return;
            }
            // --- after building newRow and checking duplicates ---
            const available =
                Number(data.available_quantity ?? 0);

            if (available <= 0) {
                handleApiError({ Error: 'No stock available for this batch.' });
                return;
            }

            // keep the newRow around and open the small qty dialog
            setScannedNewRow(newRow);
            setScannedAvailableQty(available);
            setScannedQtyValue(null); 
            setTimeout(() => {
                setScannedQtyDialogVisible(true);
            }, 60);

        } catch (err) {
            handleApiError(err);
        }
    };

    const confirmScannedQty = () => {
        if (!scannedNewRow) return;

        // If state hasn't updated yet (user typed then pressed Enter),
        // fall back to reading the DOM input value from scannedQtyRef.
        let typedValue = scannedQtyValue;
        try {
            // scannedQtyRef is provided to InputNumber via inputRef, so it should be the underlying <input>
            const dom = scannedQtyRef?.current;
            if ((typedValue === null || typeof typedValue === 'undefined' || typedValue === 0) && dom) {
                const domRaw = dom.value ?? dom.getAttribute?.('value') ?? '';
                typedValue = domRaw !== '' ? Number(domRaw) : typedValue;
            }
        } catch (err) {
            // ignore and continue with scannedQtyValue
        }

        const qty = Number(typedValue || 0);

        if (!qty || qty <= 0) {
            handleApiError({ Error: 'Please enter a valid quantity.' });
            return;
        }
        if (scannedAvailableQty && qty > scannedAvailableQty) {
            handleApiError({ Error: `Quantity cannot exceed available (${scannedAvailableQty}).`});
            return;
        }

        // build final item
        const itemToInsert = {
            ...scannedNewRow,
            quantity: qty,
            total: lineTotal(qty, scannedNewRow.price),
        };

        // prevent duplicates again (just in case)
        const duplicate = invoice.items.find(i =>
            i.product?.id === itemToInsert.product.id &&
            String(i.batch_number || '').toUpperCase() === String(itemToInsert.batch_number).toUpperCase()
        );
        if (duplicate) {
            // focus existing row quantity and notify
            bumpRow(duplicate.id);
            handleApiSuccess('Batch already present on invoice — update the quantity.');
            // close dialog
            setScannedQtyDialogVisible(false);
            setScannedNewRow(null);
            return;
        }

        // Insert new row (replace first placeholder row if present)
        setInvoice(prev => {
            const items = [...prev.items];
            const placeholderIdx = items.findIndex(it => !it.product);
            if (placeholderIdx >= 0) {
                items.splice(placeholderIdx, 1, itemToInsert);
            } else {
                items.push(itemToInsert);
            }
            return { ...prev, items };
        });

        // cleanup & return focus to scan input (use RAF + small timeout for reliability)
        setScannedQtyDialogVisible(false);
        setScannedNewRow(null);
    };


    const cancelScannedQty = () => {
        setScannedQtyDialogVisible(false);
        setScannedNewRow(null);
    };

    const fetchInvoices = async (customerId) => {
        try {
            const response = await axiosInstance.get(`/api/invoices/history-for-customer/${customerId}/`);
            setInvoices(response.data);

            // Automatically open the dialog if prescriptionId exists
            if (invId) {
                const invoiceToOpen = await axiosInstance.get(`/api/invoices/${invId}/`);
                if (invoiceToOpen.data) {
                    setInvoice(invoiceToOpen.data);
                    hydrateOperatorFromInvoice(invoiceToOpen.data);
                    setIsReadOnly(invoiceToOpen.data.status !== 'Open');
                    initCommittedMaps(invoiceToOpen.data);
                    setFilteredPrescriptions((prev) => {
                        const currentPrescription = invoiceToOpen.data.prescription ? [invoiceToOpen.data.prescription] : [];
                        return currentPrescription;
                    });
                    setDialogVisible(true);
                    setDialogName('Invoice Details');
                }
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const setOrgSettings = async () => {
        const cachedOrgSettings = localStorage.getItem('organizationSettings');

        if (cachedOrgSettings) {
            const parsedSettings = JSON.parse(cachedOrgSettings);
            const operatorAuthConfig = parsedSettings?.custom_json_settings?.operator_authentication;
            const operatorAuthEnabled =
                operatorAuthConfig?.enabled === true &&
                Array.isArray(operatorAuthConfig?.methods) &&
                operatorAuthConfig.methods.length > 0;
            const overrideApprovalRequired = parsedSettings.price_override_approval;
            const discountApprovalRequired = parsedSettings.discount_approval;
            const exceptionalAllowed = typeof parsedSettings.custom_json_settings.allow_exceptional_inventory !== 'undefined'
                ? parsedSettings.custom_json_settings.allow_exceptional_inventory
                : false;
            setAllowExceptional(exceptionalAllowed);
            if (operatorAuthEnabled){setOperatorAuthEnabled(true)}
            else {setOperatorAuthEnabled(false)}
            if (overrideApprovalRequired){setPriceEditable(false)}
            else {setPriceEditable(true)}
            if (discountApprovalRequired){setDiscountEditable(false)}
            else{setDiscountEditable(true)}
            setoverrideApproval(overrideApprovalRequired);
            setdiscountApproval(discountApprovalRequired);
        } else {
            try {
                const response = await axiosInstance.get('/api/orgsettings/');
                const orgSettings = response.data;
                const exceptionalAllowed = typeof orgSettings.custom_json_settings.allow_exceptional_inventory !== 'undefined'
                    ? orgSettings.custom_json_settings.allow_exceptional_inventory
                    : false;
                setAllowExceptional(exceptionalAllowed);
                setoverrideApproval(orgSettings.price_override_approval);
                setdiscountApproval(orgSettings.discount_approval);
                localStorage.setItem('organizationSettings', JSON.stringify(orgSettings));
            } catch (error) {
                console.error("Failed to fetch user organization settings", error);
            }
        }
    }

    const handleRevise = () => {
        alert('Revising not enabled here!!')
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();

        const endpoint =
            passwordPurpose === "admin"
                ? "/api/verify-admin-password/"
                : "/api/verify-override/";

        try {
            const response = await axiosInstance.post(endpoint, { password });

            if (response.data.isPasswordCorrect) {
                setPassword('');
                setPasswordDialogVisible(false);

                if (passwordPurpose === "override") {
                    // Unlock override options
                    setPriceEditable(true);
                    setDiscountEditable(true);
                } else if (passwordPurpose === "admin") {
                    // Proceed with setting the pharmacist-only product
                    setInvoice((prev) => {
                        const updatedItems = prev.items.map(item =>
                            item.id === pendingItemId
                                ? { ...item, product: pendingProduct }
                                : item
                        );
                        return { ...prev, items: updatedItems };
                    });
                }
            } else {
                handleApiError({ Error: "Incorrect password. Please try again." });
            }
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleSearchCustomer = (query) => {
        axiosInstance
                .get(`/api/customerq?query=${query}`)
                .then((res) => {
                    const processedSuggestions = res.data.map((cust) => ({
                        ...cust,
                        displayLabel: `${cust.first_name} ${cust.last_name}`,
                    }));
                    setFilteredCustomers(processedSuggestions);
                })
                .catch((error) => handleApiError(error));
    }

    const handleSearchPrescriptions = async (customerId) => {
        if (customerId) {
            try {
                const response = await axiosInstance.get(`api/prescriptions/active-for-customer/${customerId}/`);
                const prescriptions = response.data.map((prescription) => ({
                    id: prescription.id,
                    label: `${prescription.id} - Dr. ${prescription.doctor_name || '(N/A)' } (${new Date(prescription.date).toLocaleDateString()})`,
                    items: prescription.items
                }));
                setFilteredPrescriptions(prescriptions);
            } catch (error) {
                handleApiError(error);
            }
        } else {
            setFilteredPrescriptions([]);
        }
    };

    function isDuplicateItem(productId, batchNumber, itemId = null) {
        return invoice.items.some(
            item =>
                item.product?.id === productId &&
                item.batch_number === batchNumber &&
                (itemId === null || item.id !== itemId)
        );
    }

    // Helper: are all returned batches effectively "exceptional"?
    const allBatchesMarkedExceptional = (batches = [], activeExceptional) => {
        if (!batches || !batches.length) return false;

        // If server explicitly set 'exceptional' flag per batch, use that.
        const allHaveFlag = batches.every(b => typeof b.exceptional !== 'undefined');
        if (allHaveFlag) {
            return batches.every(b => !!b.exceptional);
        }

        // Otherwise, if we have activeExceptional (from server), treat batches
        // belonging to that inventory_id as exceptional.
        if (activeExceptional && activeExceptional.inventory_id) {
            return batches.every(b => b.inventory_id === activeExceptional.inventory_id);
        }

        return false;
    };

    // 1️⃣ fetchBatchAllocations: single call returns allocations, exceptional, excluded, insufficient
    const fetchBatchAllocations = async (productId, quantity, itemId, productObj) => {
        try {
            const res = await axiosInstance.post('/api/inventory/allocate-batches/', {
            product_id: productId,
            quantity: quantity,
            });

            const {
            allocations = [],
            excluded_batches: excluded = [],
            insufficient = false,
            exceptional = { active: false },
            has_more_batches = false,
            } = res.data || {};

            // Helper to apply "use new batches" path; if partial, create remainder + open exceptional
            const applyAllocations = () => {
                // SINGLE batch
                if (allocations.length === 1) {
                    if (isDuplicateItem(productId, allocations[0].batch_number, itemId)) {
                        handleApiError({ Error: "Duplicate product & batch entry found in invoice!" });
                        return;
                    }
                    const allocatedQty = allocations[0].allocated_quantity || 0;
                    const newId = Date.now() + Math.random();
                    let remainderId = null;
                    setInvoice(prev => {
                        const idx = prev.items.findIndex(i => i.id === itemId);
                        if (idx === -1) return prev;
                        const original = prev.items[idx];

                        const newBatchItem = {
                            ...original,
                            id:           newId,
                            product:      productObj,
                            quantity:     allocatedQty,
                            price:        allocations[0].selling_price,
                            batch_number: allocations[0].batch_number,
                            expiry_date:  allocations[0].expiry_date,
                            price_list:   allocations[0].price_list_pk,
                            inventory:    allocations[0].inventory_id,
                            total:        lineTotal(allocatedQty, allocations[0].selling_price),
                        };

                        const newItems = [...prev.items];

                        // If server says insufficient, generate a remainder line
                        const remainder = Math.max(0, quantity - allocatedQty);
                        if (insufficient && remainder > 0) {
                            remainderId = Date.now() + Math.random();
                            tempExceptionalIdsRef.current.add(remainderId);
                            const remainderItem = {
                                ...original,
                                id: remainderId,
                                product: productObj,
                                quantity: remainder,
                                price: '',
                                batch_number: null,
                                expiry_date: null,
                                price_list: null,
                                inventory: null,
                                total: 0,
                            };
                            newItems.splice(idx, 1, newBatchItem, remainderItem);

                            // Open exceptional for the remainder
                            setTimeout(() => {
                                    const nm = productObj?.name || `#${productObj?.id}`;
                                    if (exceptional?.active) {
                                        openExceptionalDialog(
                                        'existing',
                                        { itemId: remainderId, productObj, productId, qty: remainder },
                                        exceptional
                                        );
                                    } else {
                                        if (!allowExceptional) {
                                            handleApiError({ Error: `${nm}: No stock available and adding exceptional inventory is disabled by organization settings.` });
                                            return;
                                        }
                                        confirmWithNav({
                                            message: `Not enough stock available for ${nm}.\n\nDo you have in-hand stocks now to add inventory?`,
                                            header: `No Stock - ${nm}`,
                                            icon: 'pi pi-exclamation-triangle',
                                            acceptLabel: 'Add Inventory',
                                            rejectLabel: 'Cancel',
                                            accept: () => openExceptionalDialog(
                                                exceptional?.active ? 'existing' : 'new',
                                                { itemId: remainderId, productObj, productId, qty: remainder },
                                                exceptional?.active ? exceptional : null
                                            ),
                                            reject: () => {
                                                setInvoice(prev => ({ ...prev, items: prev.items.filter(it => it.id !== remainderId) }));
                                                setManualBatchMap(p => { const n = { ...p }; delete n[remainderId]; return n; });
                                                setBatchOptionsMap(p => { const n = { ...p }; delete n[remainderId]; return n; });
                                                setExcludedBatchesMap(p => { const n = { ...p }; delete n[remainderId]; return n; });
                                                tempExceptionalIdsRef.current.delete(remainderId);
                                            },
                                        });
                                    }
                                    return;
                            }, 0);
                        } else {
                            newItems.splice(idx, 1, newBatchItem);
                        }
                        return { ...prev, items: newItems };
                    });

                    setManualBatchEnabledFlag(prev => ({ ...prev, [newId]: !!has_more_batches }));
                    setManualBatchMap(prev => ({ ...prev, [newId]: false }));
                    setBatchOptionsMap(prev => ({ ...prev, [newId]: [] }));

                        if (excluded.length > 0) {
                            setExcludedBatchesMap(prev => {
                                const next = { ...prev };
                                if (remainderId) next[remainderId] = excluded;
                                else            next[newId]       = excluded;
                                return next;
                            });
                            handleApiError({ Error: `Some batches were skipped due to expiry (${excluded.length} blocked).`, critical: true  });
                        }
                        return;
                }

                    // MULTI-batch: open dialog; if insufficient handle remainder on confirm
                    setPendingItemId(itemId);
                    setPendingProduct(productObj);
                    setPendingQty(quantity);
                    setBatchAllocations(allocations);
                    setBatchDialogVisible(true);
                    setPendingExceptional(exceptional?.active ? exceptional : null);

                    if (excluded.length > 0) {
                        setExcludedBatchesMap(prev => ({ ...prev, [itemId]: excluded }));
                        handleApiError({ Error: `Some batches were skipped due to expiry (${excluded.length} blocked).` });
                    }
            };

            // A) No allocations → just ask to add inventory (prefill if exceptional exists)
            if (!allocations || allocations.length === 0) {
                const nm = productObj?.name || `#${productObj?.id}`;
                const msg =
                    excluded.length > 0
                    ? `No stock available for ${nm}. (${excluded.length} batches blocked by expiry rules)\n\nAdd inventory for old stocks now?`
                    : `No stock available for ${nm}.\n\nAdd inventory for old stocks now?`;
                if (exceptional?.active) {
                    openExceptionalDialog(
                    'existing',
                    { itemId, productObj, productId, qty: quantity },
                    exceptional
                    );
                } else {
                    if (!allowExceptional) {
                        handleApiError({ Error: `${nm}: No stock available, so the line was removed from the invoice (adding old stock is switched off in organization settings).` });
                        dropPlaceholderRow(itemId);
                        return;
                    }
                    confirmWithNav({
                        message: msg,
                        header: 'No Stock',
                        icon: 'pi pi-exclamation-triangle',
                        acceptLabel: 'Add Inventory',
                        rejectLabel: 'Cancel',
                        accept: () => openExceptionalDialog(
                        exceptional?.active ? 'existing' : 'new',
                        { itemId, productObj, productId, qty: quantity },
                        exceptional?.active ? exceptional : null
                        ),
                        reject: () => dropPlaceholderRow(itemId),
                    });
                }
                return;
            }

            // B) Allocations exist AND an active exceptional exists → let user choose
            if (exceptional?.active) {
                const nm = productObj?.name || `#${productObj?.id}`;
                const allExceptional = allBatchesMarkedExceptional(allocations, exceptional);

                const totalAllocated = (allocations || []).reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);

                if (allExceptional && totalAllocated >= quantity) {
                    applyAllocations();
                    return;
                }

                const hasNonExceptional = (allocations || []).some(a => {
                    if (typeof a.exceptional !== 'undefined') return !a.exceptional;
                    return exceptional?.inventory_id ? a.inventory_id !== exceptional.inventory_id : false;
                });

                if (hasNonExceptional) {
                    confirmWithNav({
                    header: `Choose stock source — ${nm}`,
                    message: `New batches and OLD (exceptional) stock exist for ${nm}.\nWhich one do you want to use?`,
                    acceptLabel: `Use OLD (exceptional)`,
                    rejectLabel: `Use NEW`,
                    accept: () => {
                        // compute total allocated in the allocations list and only exceptionalize the remainder
                        const totalAllocated = (allocations || []).filter(a => a.exceptional === true).reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);
                        const remainder = Math.max(0, Number(quantity || 0) - totalAllocated);
                        if (remainder > 0) {
                            // ensure we queue only the remainder for exceptional flow
                            setRxExceptionalQueue([{ itemId, productObj, productId, qty: remainder }]);
                            openExceptionalDialog('existing', { itemId, productObj, productId, qty: remainder }, exceptional);
                        } else {
                            // nothing left to exceptional — just apply the allocations
                            applyAllocations();
                        }
                    },
                    reject: () => { inactivateActiveExceptional(productId).catch(()=>{}); applyAllocations(); },
                    });
                    return;
                }
                applyAllocations();
                return;
            }

            // C) No exceptional → proceed with allocations (and handle partial remainder if any)
            applyAllocations();

        } catch (err) {
            handleApiError(err);
            setInvoice(prev => ({
                ...prev,
                items: prev.items.map(item =>
                    item.id === itemId ? { ...item, batch_number: null, price: '', inventory: null, price_list: null, total: 0 } : item
                )
            }));
        }
    };

const allocateDeltaForExistingItem = async (itemId, productObj, deltaQty, batch_number) => {
  if (!deltaQty || deltaQty <= 0) return;

  try {
    const { data } = await axiosInstance.post('/api/inventory/allocate-batches/', {
      product_id: productObj.id,
      quantity: deltaQty,
      batch_number: batch_number || null
    });

    const {
      allocations = [],
      excluded_batches: excluded = [],
      insufficient = false,
      exceptional = { active: false }
    } = data || {};

    if (!allocations.length) {
      // same behavior as before: open exceptional flow (only for the delta)
      const nm = productObj?.name || `#${productObj?.id}`;
      if (exceptional?.active) {
        openExceptionalDialog('existing', { itemId, productObj, productId: productObj.id, qty: deltaQty }, exceptional);
      } else {
        if (!allowExceptional) {
          handleApiError({ Error: `${nm}: No stock available and adding exceptional inventory is disabled by organization settings.` });
        } else {
          confirmWithNav({
            message: `No stock available for extra quantity of ${nm}.\n\nAdd OLD (exceptional) inventory now?`,
            header: 'No Stock',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Add Inventory',
            rejectLabel: 'Cancel',
            accept: () => openExceptionalDialog('new', { itemId, productObj, productId: productObj.id, qty: deltaQty }, null),
          });
        }
      }
      return;
    }

    setInvoice(prev => {
      const items = [...prev.items];
      const baseIdx = items.findIndex(i => i.id === itemId);
      if (baseIdx === -1) return prev;
      const base = items[baseIdx];
      let totalAllocated = 0;
      // 1) Apply allocations that match the base row's batch by INCREASING the base qty
      const sameBatch = allocations.filter(b => b.batch_number === base.batch_number);
      if (sameBatch.length) {
        const addQty = sameBatch.reduce((s, b) => s + (Number(b.allocated_quantity) || 0), 0);
        if (addQty > 0) {
          totalAllocated += addQty;
          const savedBaseQty = Number(savedQtyRef.current[itemId] ?? 0);
          items[baseIdx] = {
            ...base,
            // keep batch number the same; adopt selling price if backend insists, otherwise keep base.price
            price: sameBatch[0].selling_price ?? base.price,
            price_list: sameBatch[0].price_list_pk ?? base.price_list,
            inventory: sameBatch[0].inventory_id ?? base.inventory,
            quantity: savedBaseQty + addQty,
            total: (savedBaseQty + addQty) * (sameBatch[0].selling_price ?? base.price ?? 0),
            __qtyKey: (base.__qtyKey || 0) + 1,
          };
        }
      }
      // 2) For different batches, MERGE into an existing row with same product+batch if present; else add a new line
      const diffBatches = allocations.filter(b => b.batch_number !== base.batch_number);
      diffBatches.forEach(b => {
        const addQty = Number(b.allocated_quantity) || 0;
        if (!addQty) return;
        totalAllocated += addQty;
        // if a row for same product+batch exists (other than base), merge into it
        const dupIdx = items.findIndex(
          (it, idx) =>
            idx !== baseIdx &&
            it.product?.id === productObj.id &&
            it.batch_number === b.batch_number
        );
        if (dupIdx > -1) {
          const row = items[dupIdx];
          const savedDupQty = Number(savedQtyRef.current[row.id] ?? row.quantity ?? 0);
          items[dupIdx] = {
            ...row,
            price: b.selling_price ?? row.price,
            price_list: b.price_list_pk ?? row.price_list,
            inventory: b.inventory_id ?? row.inventory,
            quantity: b.allocated_quantity,
            total: b.allocated_quantity * (b.selling_price ?? row.price ?? 0),
            __qtyKey: (row.__qtyKey || 0) + 1,
          };
        } else {
          // otherwise, add a new line just for this different batch
            const savedBaseQty = Number(savedQtyRef.current[itemId] ?? 0);
            items[baseIdx] = {
            ...base,
            quantity: savedBaseQty,
            total: savedBaseQty * (base.price || 0),
            __qtyKey: (base.__qtyKey || 0) + 1,
            };

          items.splice(baseIdx + 1, 0, {
            ...base,
            id: Date.now() + Math.random(),
            parent_item_id: itemId, 
            product: productObj,
            quantity: b.allocated_quantity,
            price: b.selling_price,
            batch_number: b.batch_number,
            expiry_date: b.expiry_date,
            price_list: b.price_list_pk,
            inventory: b.inventory_id,
            total: b.allocated_quantity * b.selling_price,
          });
        }
      });

      return { ...prev, items };
    });
    bumpRow(itemId);

    // 3) Handle remainder (if allocator couldn't fully satisfy delta)
    const totalAllocated = allocations.reduce((s, a) => s + (Number(a.allocated_quantity) || 0), 0);
    const remainder = Math.max(0, Number(deltaQty) - totalAllocated);
    if (remainder > 0 || (insufficient && remainder >= 0)) {
      const nm = productObj?.name || `#${productObj?.id}`;
      if (exceptional?.active) {
        openExceptionalDialog('existing', { itemId, productObj, productId: productObj.id, qty: remainder || deltaQty }, exceptional);
      } else if (allowExceptional) {
        confirmWithNav({
          message: `Not enough stock for the extra quantity of ${nm}.\n\nAdd OLD (exceptional) inventory for the remainder (${remainder})?`,
          header: `Insufficient Stock — ${nm}`,
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Add Inventory',
          rejectLabel: 'Cancel',
          accept: () => openExceptionalDialog('new', { itemId, productObj, productId: productObj.id, qty: remainder }, null),
        });
      } else {
        handleApiError({ Error: `${nm}: Not enough stock for the extra quantity and adding exceptional inventory is disabled by organization settings.` });
      }
    }

    // 4) Show excluded-batch notice (if any)
    if ((excluded || []).length > 0) {
      setExcludedBatchesMap(prev => {
        const next = { ...prev };
        next[itemId] = [ ...(next[itemId] || []), ...excluded ];
        return next;
      });
      handleApiError({ Error: `Some batches were skipped due to expiry (${excluded.length} blocked).` });
    }
  } catch (err) {
    handleApiError(err);
  }
};

    const reducePendingForItem = async (itemId, reduceBy) => {
        if (reduceBy <= 0) return;

        setInvoice(prev => {
            const items = prev.items.map(item => {
                if (item.id !== itemId) return item;

                const newQty = Math.max(0, Number(item.quantity || 0) - reduceBy);

                return {
                    ...item,
                    quantity: newQty,
                    total: newQty * (item.price || 0),
                    __qtyKey: (item.__qtyKey || 0) + 1,
                };
            });

            return { ...prev, items };
        });

        bumpRow(itemId);
    };

    const handleBatchAllocationChange = (rowIndex, value) => {
        setBatchAllocations(prev =>
            prev.map((b, idx) =>
                idx === rowIndex ? { ...b, allocated_quantity: value } : b
            )
        );
    };

    const handleBatchConfirm = () => {
        // validate duplicates
        let hasDuplicate = false;
        batchAllocations.forEach(b => {
            if (isDuplicateItem(pendingProduct.id, b.batch_number, pendingItemId)) {
            hasDuplicate = true;
            }
        });
        if (hasDuplicate) {
            handleApiError({ Error: "Duplicate product & batch entry found in invoice!" });
            return;
        }

        const totalAllocated = batchAllocations.reduce((acc, b) => acc + (b.allocated_quantity || 0), 0);
        if (totalAllocated > pendingQty) {
            handleApiError({ Error: `Allocated quantity (${totalAllocated}) exceeds requested (${pendingQty}).` });
            return;
        }
        const remainder = pendingQty - totalAllocated;

        // Build new rows and insert them (replace the original pending row)
        let remainderId = null;
        setInvoice(prev => {
            const idx = prev.items.findIndex(i => i.id === pendingItemId);
            if (idx === -1) return prev;

            const original = prev.items[idx];

            const newBatchItems = batchAllocations
            .filter(b => b.allocated_quantity > 0)
            .map(b => ({
                ...original,
                id:           Date.now() + Math.random(),
                product:      pendingProduct,
                quantity:     b.allocated_quantity,
                price:        b.selling_price,
                batch_number: b.batch_number,
                expiry_date:  b.expiry_date,
                price_list:   b.price_list_pk,
                inventory:    b.inventory_id,
                total:        b.allocated_quantity * b.selling_price,
            }));

            const newItems = [...prev.items];
            if (remainder > 0) {
            remainderId = Date.now() + Math.random();
            tempExceptionalIdsRef.current.add(remainderId);
            const remainderItem = {
                ...original,
                id: remainderId,
                product: pendingProduct,
                quantity: remainder,
                price: '',
                batch_number: null,
                expiry_date: null,
                price_list: null,
                inventory: null,
                total: 0,
            };
            newItems.splice(idx, 1, ...newBatchItems, remainderItem);
            } else {
            newItems.splice(idx, 1, ...newBatchItems);
            }

            return { ...prev, items: newItems };
        });

        setBatchAllocations([]);
        setBatchDialogVisible(false);
        setExcludedBatchesMap(prev => {
            const next = { ...prev };
            if (next[pendingItemId]?.length) {
                const excl = next[pendingItemId];
                delete next[pendingItemId];
                if (remainderId) next[remainderId] = excl; // show icon on the leftover row
            }
            return next;
        });

        // Resume queued choices ONLY when there is no top-up dialog to show
        // (If we open exceptional top-up now, we keep the queue paused to avoid overlapping modals)
        if (remainder === 0 && choiceAskNextRef.current) {
        const rest = (pendingChoiceQueueRef.current || []).slice(0);
        pendingChoiceQueueRef.current = [];
        setTimeout(() => choiceAskNextRef.current(rest), 0);
        }

        // If there is remaining qty, open exceptional dialog for the remainder
        if (remainder > 0) {
            if (suppressExceptionalAfterSplitRef.current) {
                // User explicitly chose "Use New" → do not prompt exceptional here.
                suppressExceptionalAfterSplitRef.current = false;
                // Resume the paused choice queue
                if (choiceAskNextRef.current) {
                    const rest = (pendingChoiceQueueRef.current || []).slice(0);
                    pendingChoiceQueueRef.current = [];
                    setTimeout(() => choiceAskNextRef.current(rest), 0);
                }
            } else {
                // Normal path: open exceptional dialog for the remainder
                setTimeout(() => {
                    openExceptionalDialog(
                        pendingExceptional?.active ? 'existing' : 'new',
                        { itemId: remainderId, productObj: pendingProduct, productId: pendingProduct.id, qty: remainder },
                        pendingExceptional?.active ? pendingExceptional : null
                    );
                }, 0);
            }
        }
    }  

    const handleItemChange = async (itemId, field, value) => {
        // snapshot to compute delta safely outside setState
        const currentItem = invoice.items.find(i => i.id === itemId);

        // --- DELTA path for quantity on an already-allocated row ---
        if (field === 'quantity' && currentItem?.product?.id && currentItem?.batch_number) {

            const saved   = Number(savedQtyRef.current[currentItem.id] ?? 0);
            const target  = Number(value || 0);
            const netDelta = target - saved;

            if (netDelta > 0) {
                // prevent duplicate call while an allocation is already running
                if (inFlightAllocRef.current[currentItem.id]) {
                    return;
                }
                inFlightAllocRef.current[currentItem.id] = true;
                try {
                    await allocateDeltaForExistingItem(currentItem.id, currentItem.product, netDelta, currentItem.batch_number);
                } catch (e) {
                    throw e;
                } finally {
                    inFlightAllocRef.current[currentItem.id] = false;
                }
                // IMPORTANT: do NOT write back item.quantity here
                return;
            }

            if (netDelta < 0) {
                const newQty = Math.max(0, Number(value || 0));

                setInvoice(prev => {
                    const items = prev.items.map(item => {
                        if (item.id !== itemId) return item;

                        return {
                            ...item,
                            quantity: newQty,
                            total: newQty * (item.price || 0),
                            __qtyKey: (item.__qtyKey || 0) + 1,
                        };
                    });

                    return { ...prev, items };
                });

                return;
            }

            // netDelta === 0 -> nothing to do, and crucially, don't write the field either
            return;
        }

        setInvoice((prev) => {
            const updatedItems = prev.items.map(item =>
                item.id === itemId ? { ...item, [field]: value } : item
            );
            let item = updatedItems.find(item => item.id === itemId);

            // Frequency calculation
            const frequencyMap = {
                BD: 2,
                TDS: 3,
                QDS: 4,
                Mane: 1,
                Nocte: 1,
                QHS: 1,
                Q4H: 6,
                Q6H: 4,
                Q8H: 3,
                Q12H:2,
                EOD: .5,
            };

            // If product or quantity changed, trigger batch allocation
            if ((field === 'product' && value?.id && item.quantity > 0) || (field === 'quantity' && value > 0 && item.product?.id && (!item?.batch_number || !invoice?.id))) {
                const targetProduct = field === "product" ? value : item.product;
                fetchBatchAllocations(
                    field === "product" ? value.id : item.product.id,
                    field === "quantity" ? value : item.quantity,
                    itemId,
                    targetProduct
                );

                return { ...prev, items: updatedItems };
            }

            // If product changed, update related fields
            if (field === 'product') {
                if (value?.pharmacist_only && !isAdminUser) {
                    setPendingItemId(itemId); 
                    setPendingProduct(value); 
                    setPasswordPurpose("admin");
                    setPasswordDialogHeader("Enter Admin Password for restricted product selection")
                    setPasswordDialogVisible(true); 
                    return prev; 
                }
                item = {
                    ...item,
                    product: value,
                    total: (item.quantity || 0) * (item.price || 0),
                };
                const idx = updatedItems.findIndex(i => i.id === itemId);
                updatedItems[idx] = item;
            }

            if (
                (field === "dosage" || field === "duration" || field === "frequency") &&
                item.product?.uses &&
                (item.product.uses === "Tablet" || item.product.uses === "Capsule")
            ) {
                const frequencyValue = frequencyMap[item.frequency] || null;
                if (frequencyValue && item.dosage && item.duration) {
                    item = {
                        ...item,
                        quantity: item.dosage * item.duration * frequencyValue,
                        total: (item.dosage * item.duration * frequencyValue * item.price) || 0,
                    };
                    const idx = updatedItems.findIndex(i => i.id === itemId);
                    updatedItems[idx] = item;
                }
            }

            // When quantity or price changes, update total and validate against inventory
            if (field === 'quantity' || field === 'price') {
                item = {
                    ...item,
                    total: (item.quantity || 0) * (item.price || 0),
                };
                const idx = updatedItems.findIndex(i => i.id === itemId);
                updatedItems[idx] = item;

                // Inventory check: if info available, validate and set error if needed
                if (item.product?.id && inventoryInfo[item.product.id]) {
                    const available = inventoryInfo[item.product.id].quantity || 0;
                    if ((field === 'quantity' && value > available) || (field === 'price' && item.quantity > available)) {
                        setInventoryError((prev) => ({
                            ...prev,
                            [itemId]: `Only ${available} items in stock.`,
                        }));
                        // Auto-correct the quantity
                        item.quantity = available;
                        updatedItems[idx] = item;
                    } else {
                        setInventoryError((prev) => ({
                            ...prev,
                            [itemId]: null,
                        }));
                    }
                }
            }
            if (field === "batch") {
            const batch = value;

            setInvoice((prev) => {
                const updatedItems = prev.items.map((item) =>
                item.id === itemId
                    ? {
                        ...item,
                        batch_number: batch.batch_number,
                        inventory: batch.inventory_id,
                        expiry_date: batch.expiry_date,
                        price: batch.selling_price,
                        price_list: batch.price_list_pk,
                        total: item.quantity * batch.selling_price,
                    }
                    : item
                );
                return { ...prev, items: updatedItems };
            });
            }

            setSelectedItem(null);
            return { ...prev, items: updatedItems };
        });
    };

    const handlePresItemChange = (itemId, field, value) => {
        setPrescription((prev) => {
            const updatedItems = prev.items.map(item =>
                item.id === itemId ? { ...item, [field]: value } : item
            );
            let item = updatedItems.find(item => item.id === itemId);

            const frequencyMap = FREQUENCY_MAP;

            // Calculate quantity only if product.uses is Tablet or Capsule and frequency is valid
            if (
                (field === "dosage" || field === "duration" || field === "frequency") &&
                item?.product?.uses &&
                (item.product.uses === "Tablet" || item.product.uses === "Capsule")
            ) {
                const frequencyValue = frequencyMap[item.frequency] || null;
                if (frequencyValue && item.dosage && item.duration) {
                    item.quantity = item.dosage * item.duration * frequencyValue;
                    // Put the updated item back in the array
                    const idx = updatedItems.findIndex(i => i.id === itemId);
                    updatedItems[idx] = item;
                }
            }

            return { ...prev, items: updatedItems };
        });
    };

    
    const handleCusChange = (e, field) => {
        const value = e.target ? e.target.value : e.value;
        setCustomer((prev) => {
            const updatedCustomer = { ...prev, [field]: value };
    
            // Set default title based on gender
            if (field === "gender") {
                if (value === "M") {
                    updatedCustomer.title = "Mr.";
                } else if (value === "F") {
                    updatedCustomer.title = "Mss.";
                }
            }
    
            return updatedCustomer;
        });
    };

    const handleRowClick = async  (rowData) => {
        try {
            // Fetch the full prescription details (including customer and product details)
            const response = await axiosInstance.get(`/api/invoices/${rowData.id}/`);
            const detailedinvoice = response.data;
            initCommittedMaps(detailedinvoice);
            // Set the detailed prescription data to state
            setInvoice(detailedinvoice);
            hydrateOperatorFromInvoice(detailedinvoice);
            setFilteredPrescriptions((prev) => {
                const currentPrescription = detailedinvoice.prescription ? [detailedinvoice.prescription] : [];
                return currentPrescription;
            });
            // Store upload data for the toolbar button — panel opens inside the dialog
            if (detailedinvoice.portal_prescription_upload?.file_url) {
                setInvoiceUploadData(detailedinvoice.portal_prescription_upload);
            } else {
                setInvoiceUploadData(null);
            }
            setIsReadOnly(detailedinvoice.status === 'Open'? false : true); // Enable read-only mode
            setDialogVisible(true);
            setDialogName('Invoice Details');
        } catch (error) {
            handleApiError(error);
        }
    };

    const calculatePrices = (items = invoice.items, discount = invoice.discount, isdiscount_per = invoice.isdiscount_per) => {
        let total_price = 0;
        let discountableBase = 0;
    
        items.forEach((item) => {
            const line = Number((item.quantity * item.price).toFixed(2));
            total_price = money(total_price + line);
            const isDiscountable = item?.product?.discountable !== false;
            if (isDiscountable) discountableBase += line;
        });
    
        const discountAmount = isdiscount_per
            ? discountableBase * ((discount || 0) / 100)
            : (discount || 0);
    
        const taxRate = 0; // 0% tax 
        const tax_amount = (total_price - discountAmount) * taxRate;
    
        const net_price = total_price - discountAmount + tax_amount;
    
        setInvoice((prev) => ({
            ...prev,
            total_price,
            discountAmount,
            tax_amount,
            net_price,
        }));
    };

    // Auto-apply a customer's loyalty discount (percentage) and recalc
    const applyCustomerLoyaltyDiscount = (cust) => {
        const pct = Number(cust?.loyalty_discount_percent ?? 0);
        if (!Number.isNaN(pct) && pct > 0) {
            setInvoice(prev => ({ ...prev, isdiscount_per: true, discount: pct }));
        calculatePrices(undefined, pct, true);
        } else {
            setInvoice(prev => ({ ...prev, discount: null }));
        }
    };

    const addNewItem = () => {
        const newId = Date.now() + Math.random();
        setInvoice((prev) => ({
            ...prev,
            items: [
                {
                    id: newId,
                    product: null, quantity: '', price: '', dosage:'', frequency:'', duration: prev.duration || ''
                },
                ...prev.items,
            ],
        }));
        setSelectedItem(null);
        setTimeout(() => {
            prodInputRefsInv.current[newId]?.focus();
        }, 50);
    };

    const addNewPresItem = () => {
        const newId = Date.now() + Math.random();
        setPrescription((prev) => ({
            ...prev,
            items: [
                {
                    id: newId,
                    product: null,
                    quantity: '',
                    dosage: '1',
                    frequency: '',
                    duration: prev.duration || '',
                }, ...prev.items,
            ],
        }));
        setTimeout(() => {
            prodInputRefs.current[newId]?.focus();
        }, 100);

    };

    const handleDurationChange = (value) => {
        setInvoice((prev) => {
            const updatedItems = prev.items.map((item) => ({
                ...item,
                duration: (item.duration === null || item.duration === prev.duration) ? value : item.duration, 
            }));
    
            return { ...prev, duration: value, items: updatedItems };
        });
    };
    
    const handlePresDurationChange = (value) => {
        setPrescription((prev) => {
            const updatedItems = prev.items.map((item) => ({
                ...item,
                duration: (item.duration === null || item.duration === prev.duration) ? value : item.duration, 
            }));
    
            return { ...prev, duration: value, items: updatedItems };
        });
    };

    const handleDrop = async (e) => {
        e.preventDefault(); 
        if (!invoice || !invoice.items || invoice.items.length === 0) {
            handleApiError({ 'Error': 'Invoice must have at least one item before completing.' });
            return;
        }
        try {
                const response = await axiosInstance.post(`/api/invoices/${invoice.id}/cancel/`, {
                    operator_id: invoiceOperator?.operator_id || null,
                    operator_verification_method: invoiceOperator?.verification_method || null,
                });
                setInvoice((prev) => ({ ...prev, status: "Cancelled", is_paid: false }));
                setInvoices((prevInvoices) =>
                    prevInvoices.map((inv) =>
                        inv.id === invoice.id ? { ...inv, status: "Cancelled", is_paid: false } : inv
                    )
                );
    
                handleApiSuccess("Invoice cancelled, stock returned to inventory.");
                setDialogVisible(false);
                setInvoiceOperator(null);
                setShowOperatorAuth(false);
                setIsCreditNote(false);
            }
        catch (error) {
            handleApiError(error||{'ERR':'Invoice must have at least one item before completing.'});
        }
    };

    const handleSubmit = async (e, isCompleting) => {
        e.preventDefault(); 
        if (blockIfUnsavable()) return;
        const payload = {
            ...invoice,
            status: 'Open',
            is_paid: false, // Set is_paid only if completing
            customer: invoice.customer ? invoice.customer.id : null,
            prescription: invoice.prescription ? invoice.prescription.id : null,
            date: new Date().toISOString(),
            operator_id: invoiceOperator?.operator_id || null,
            operator_verification_method: invoiceOperator?.verification_method || null,
            portal_prescription_upload_id: portalPrescriptionUploadId.current,
            items: invoice.items.map((item) => ({
                ...item,
                product: item.product.id,
                price: Number(item.price).toFixed(2),
                total: Number((item.quantity * item.price).toFixed(2)),
                batch_number: item.batch_number,
                expiry_date: item.expiry_date,
                price_list: item.price_list,
                inventory: item.inventory,
            })),
        };
    
        try {
            const res = invoice.id
                ? await axiosInstance.put(`/api/invoices/${invoice.id}/`, payload)
                : await axiosInstance.post(`/api/invoices/`, payload);
    
            setInvoices((prev) => {
                const cleaned = {
                    ...res.data,
                    prescription: res.data.prescription?.id ?? null,
                    customer_name: res.data.customer?.displayLabel ?? null,
                };

                const updatedInvoices = invoice.id
                    ? prev.map((inv) => inv.id === invoice.id ? cleaned : inv)
                    : [cleaned, ...prev];
                    
                return updatedInvoices;
            });

            if (!isCompleting)
            {
                setInvoice(prev => ({ ...prev, ...res.data }));
                handleApiSuccess(`Invoice saved successfully!`);
            } else {
                setInvoice(res.data);
                initCommittedMaps(res.data);
                // now open payment dialog
                setShowPaymentDialog(true);
            }
            
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePrescriptionChange = async (e) => {
        try {
            const selected = e.value;
            if (!selected) {
            setInvoice(prev => ({ ...prev, prescription: null, items: [] }));
            return;
            }

            // 1️⃣ Fetch full prescription details
            const { data: details } = await axiosInstance.get(
            `/api/prescriptions/${selected.id}/`
            );

            await allocateRxIntoInvoice(details);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handlePresSubmit = async (e) => {
        e.preventDefault();
        const payload = {
            ...prescription,
            customer: prescription.customer.id,
            date: prescription.date ? format(new Date(prescription.date), 'yyyy-MM-dd') : null,
            items: prescription.items.map((item) => ({
                ...item,
                product_id: item.product.id,
            })),
        };
        try {
            const { data: newPrescription } = await axiosInstance.post(
            '/api/prescriptions/',
            payload
            );
            // Run bulk allocation directly and populate invoice rows
            await allocateRxIntoInvoice(newPrescription);
            // Close the Rx dialog and reset the small Rx form
            setPrescription({
                customer: null,
                doctor_name: '',
                duration: null,
                date: new Date(),
                items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
            });
            setPrescriptionDialogVisible(false);
            handleApiSuccess('Prescription created & batches allocated.');
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleCusSubmit = async (e) => {
        e.preventDefault();
        
        const validatedPhoneNumber = validatePhoneNumber(customer.phone_number ? customer.phone_number : '0700000000');
        if (!validatedPhoneNumber) {
            handleApiError({ Error: "Invalid phone number. Please enter a valid number." });
            return;
        }
        const formattedCustomer = {
            ...customer,
            phone_number: customer.phone_number ? validatedPhoneNumber : null,
            dob: customer.dob ? format(new Date(customer.dob), 'yyyy-MM-dd') : null, 
        };
    
        // Ensure title matches gender selection
        if (
            (customer.gender === "M" && customer.title !== "Mr." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.") ||
            (customer.gender === "F" && customer.title !== "Mss." && customer.title !== "Mrs." && customer.title !== "Dr." && customer.title !== "Ven." && customer.title !== "Rev.")
        ) {
            handleApiError({ Error: "Invalid title for the selected gender" });
            return;
        }
        try {
            const result = await axiosInstance.post("/api/customer/", formattedCustomer);
            const newCustomer= result.data;
            const formattedNewCustomer = { ...newCustomer, displayLabel: `${newCustomer.first_name} ${newCustomer.last_name}` };
            setInvoice((prev) => ({ ...prev, customer: formattedNewCustomer }));
            setFilteredCustomers((prev) => [...prev, formattedNewCustomer]);
            applyCustomerLoyaltyDiscount(formattedNewCustomer);
            setCustomer({
                title: "",
                gender: "",
                first_name: "",
                last_name: "",
                email: null,
                phone_number: null,
                address: "",
                nic: "",
                passport: "",
                dob: null,
                category: "",
                segment: "",
                loyalty: "",
                priority: 5,
                status: "Regular",
                organization: "b835b027-72ba-4c99-af9c-c8c6c1e1c732",
                relationship: "",
                relative: null,
                ralative_flg: false,
                loyalty_discount_percent: 0,
            });
            handleApiSuccess(`Customer ${result.data.first_name} ${result.data.last_name} saved successfully.`);
            setCustomerDialogVisible(false);
        } catch (error) {
            handleApiError(error);
        }
    };

    // Called by PaymentDialog after a successful payment POST
    const handlePaymentSaved = (updatedInvoice) => {
        setInvoice(updatedInvoice);
        setIsReadOnly(updatedInvoice.status === 'Open' ? false : true);
        setInvoices(prev => {
            const cleaned = {
                ...updatedInvoice,
                prescription: updatedInvoice.prescription?.id ?? null,
                customer_name: updatedInvoice.customer?.displayLabel ?? null,
            };
            return invoice.id
                ? prev.map(inv => inv.id === invoice.id ? cleaned : inv)
                : [cleaned, ...prev];
        });
        setShowPaymentDialog(false);
    };

    // A line that has no stock batch (out of stock, or the user declined to add old stock) cannot be invoiced.
    const dropPlaceholderRow = (itemId) => {
        setInvoice(prev => ({ ...prev, items: prev.items.filter(it => it.id !== itemId) }));
        setManualBatchMap(p => { const n = { ...p }; delete n[itemId]; return n; });
        setBatchOptionsMap(p => { const n = { ...p }; delete n[itemId]; return n; });
        setExcludedBatchesMap(p => { const n = { ...p }; delete n[itemId]; return n; });
        tempExceptionalIdsRef.current.delete(itemId);
    };

    // Names of lines that would be refused by the server: a product with no stock batch, or no quantity.
    const unsavableLines = () => (invoice.items || []).filter((it) => !it.inventory || !(Number(it.quantity) > 0))
        .map((it) => (!it.product ? 'An empty line (pick a product or remove it)' : `${it.product.name}${!it.inventory ? ' (no stock batch)' : ' (no quantity)'}`));
    const blockIfUnsavable = () => {
        if (!(invoice.items || []).length) {
            handleApiError({ Error: 'Add at least one product to the invoice before saving.' });
            return true;
        }
        const bad = unsavableLines();
        if (!bad.length) return false;
        handleApiError({ Error: `Cannot save yet. Remove these lines, or add stock for them first: ${bad.join(', ')}.` });
        return true;
    };

    const removeItem = (itemId) => {
        setInvoice((prev) => {
            const updatedItems = prev.items.filter((item) => item.id !== itemId);
            setInventoryError(prevErr => {
                const { [itemId]: _, ...rest } = prevErr;
                return rest;
            });
            return { ...prev, items: updatedItems };
        });
        setSelectedItem(null);
    };
    
    const removePresItem = (itemId) => {
        setPrescription((prev) => ({
            ...prev,
            items: prev.items.filter(item => item.id !== itemId),
        }));
    };

    const confirmCreditNote = () => {
        confirmWithNav({
            message: "Issuing a credit note will cancel this completed invoice and refund the payments. Do you want to proceed?",
            header: "Confirm Credit Note",
            icon: "pi pi-exclamation-triangle",
            acceptClassName: "p-button-danger",
            acceptLabel: "Yes, Proceed",
            rejectLabel: "No, Cancel",
            defaultFocus: "reject",
            accept: () => {
                if (operatorAuthEnabled) {
                    setShowOperatorAuth(true);
                    setIsCreditNote(true);
                } else {
                    setshowCreditNoteDialog(true);
                }
            },
        });
    };

    const issueCreditNote = async (invoiceId) => {
        if (!creditNoteReason.trim()) {
            handleApiError({Error:"Please enter a reason for the credit note."});
            return;
        }
        try {
            const response = await axiosInstance.post(`/api/issue-credit-note/${invoiceId}/`, {
                reason: creditNoteReason,
                operator_id: invoiceOperator?.operator_id || null,
                operator_verification_method: invoiceOperator?.verification_method || null,
            });
            handleApiSuccess("Credit note issued. New invoice created.");
            setshowCreditNoteDialog(false);
            setDialogVisible(false);
            setInvoiceOperator(null);
            setShowOperatorAuth(false);
            setIsCreditNote(false);
            navigate(`/invoices`, {
                state: {
                    invId: response.data.new_invoice_id,
                    customerId: response.data.customer
                },
            });
        } catch (error) {
            handleApiError(error);
        }
    };    

    const handleSubmitProductIssue = async () => {
        try {
            await axiosInstance.post("api/notifications/tag-product-issue/", {
                product_id: selectedItem.product.id,
                issue_description: issueDescription
            });

            handleApiSuccess("Product issue has been reported to the admin.");
            setProductIssueDialog(false);
            setIssueDescription("");
            setSelectedItem(null);
        } catch (error) {
            handleApiError(error);
        }
    };

    const handleOpenTagIssue = (product) => {
        setProductIssueDialog(true);
    };

    const currencyFilterTemplate = (options) => {
        return <InputNumber value={options.value} onChange={(e) => options.filterCallback(e.value, options.index)} mode="currency" currency="LKR" locale="en-US" />
    }
    const dateFilterTemplate = (options) => {
        const safeDate = options.value ? new Date(options.value) : null;

        return (
            <Calendar
                value={safeDate}
                onChange={(e) => options.filterCallback(e.value, options.index)}
                dateFormat="mm/dd/yy"
                showTime
                hourFormat="12"
                placeholder="MM/DD/YYYY, h:mm AM"
                mask="99/99/9999"
                showIcon
            />
        );
    };

    // GET active exceptional batch (qty can be 0) for prefill
    const fetchActiveExceptional = async (productId) => {
    try {
        const res = await axiosInstance.get('/api/inventory/exceptional/active/', {
        params: { product_id: productId },
        });
        return res?.data?.active ? res.data : null;
    } catch (e) {
        handleApiError(e);
    }
    };

    // inactivate existing exceptional if user proceeds with NEW batch when normal stock exists
    const inactivateActiveExceptional = async (productId) => {
        try {
            const info = await fetchActiveExceptional(productId);
            if (!info || !info.inventory_id) return;
            await axiosInstance.post('/api/inventory/exceptional/inactivate/', {
            batch_id: info.inventory_id,
            });
        } catch (e) {
            handleApiError(e);
        }
    };

    const openExceptionalDialog = async (mode, { itemId, productObj, productId, qty }, activeInfo = null) => {
        const isExisting = mode === 'existing';
        setExceptionalActiveInfo(activeInfo);

        // Open the dialog immediately with what we have
        exceptionalDialogRef.current?.open(mode, { itemId, productObj, productId, qty }, activeInfo);
        setExceptionalVisible(true);

        // For 'new' mode, fetch prices async and update via ref
        if (!isExisting) {
            try {
                const res = await axiosInstance.get(`/api/products/${productId}/active-pricelist/`);
                exceptionalDialogRef.current?.setPrices(
                    res.data?.purchase_price ?? 0,
                    res.data?.selling_price  ?? 0,
                );
            } catch (err) {
                // prices stay at 0, user can type them in
            }
        }
    };

    const startRxExceptionalFlow = () => {
        setTimeout(() => {
                setRxExceptionalQueue(prev => {
                    if (!prev || !prev.length) return prev;
                    const first = prev[0];
                    openExceptionalDialog(
                            first.activeExceptional?.active ? 'existing' : 'new',
                            {
                                itemId: first.itemId,
                                productObj: first.productObj,
                                productId: first.productId,
                                qty: first.qty,
                            },
                            first.activeExceptional?.active ? first.activeExceptional : null
                    );
                    return prev;
                });
        }, 0);
    };

    // Bulk allocate for a list of Rx items inside the invoice dialog
    const allocateRxIntoInvoice = async (rxDetails) => {
        // Build request body for bulk endpoint
        const productsToAllocate = (rxDetails.items || [])
            .filter(i => i?.product?.id && Number(i.quantity) > 0)
            .map(i => ({ product_id: i.product.id, quantity: Number(i.quantity) }));

        if (!productsToAllocate.length) {
            handleApiError({ Error: 'No valid prescription items to allocate.' });
            return;
        }

        try {
            const { data } = await axiosInstance.post('/api/inventory/allocate-batches-bulk/', productsToAllocate);
            // Map for product objects and requested qty
            const productById = {};
            const requestedById = {};
            rxDetails.items.forEach(i => {
                productById[i.product.id] = i.product;
                requestedById[i.product.id] = Number(i.quantity) || 0;
            });

            const finalItems = [];     
            const choiceQueue = [];     
            const noStockQ   = []; 

            // Walk server allocations
            for (const a of (data.allocations || [])) {
                const pid       = a.product_id ?? a.productId;
                const requested = requestedById[pid] || 0;
                const prodObj   = productById[pid];
                const exInfo    = a.exceptional || { active: false };
                const batches   = a.batches || [];
                const hasMore   = !!a.has_more_batches;
                const totalAllocated = batches.reduce((s, b) => s + (Number(b.allocated_quantity) || 0), 0);

                if (!batches.length) {
                    // No stock at all -> create a placeholder row and queue exceptional
                    const itemId = Date.now() + Math.random();
                    finalItems.push({
                        id: itemId,
                        product: prodObj,
                        quantity: requested,
                        price: '',
                        batch_number: null,
                        expiry_date: null,
                        price_list: null,
                        inventory: null,
                        total: 0,
                    });
                    noStockQ.push({ itemId, productObj: prodObj, productId: pid, qty: requested, activeExceptional: exInfo });
                    tempExceptionalIdsRef.current.add(itemId);
                    continue;
                }
                
                // Single batch and fully allocated -> one line
                if (batches.length === 1 && (a.fully_allocated || totalAllocated === requested)) {
                    const b = batches[0];
                    const singleLine = {
                        id: Date.now() + Math.random(),
                        product: prodObj,
                        quantity: b.allocated_quantity,
                        price: b.selling_price,
                        batch_number: b.batch_number,
                        expiry_date: b.expiry_date,
                        price_list: b.price_list_pk,
                        inventory: b.inventory_id,
                        total: (Number(b.allocated_quantity) || 0) * (Number(b.selling_price) || 0),
                    };
                    const isExceptionalBatch =
                       (typeof b.exceptional !== 'undefined')
                         ? !!b.exceptional
                         : (exInfo?.inventory_id ? b.inventory_id === exInfo.inventory_id : false);

                     if (exInfo.active && isExceptionalBatch) {
                         // Use available exceptional stock directly (no confirmation).
                         finalItems.push(singleLine);
                     } else if (exInfo.active) {
                         // Exceptional active but this is a NEW (non-exceptional) batch -> ask OLD vs NEW as before.
                         choiceQueue.push({ type: 'single', pid, prodObj, requested, singleLine, exceptional: exInfo, hasMore: hasMore, remainder: requested });
                     } else {
                         // No exceptional active -> just use the allocation
                         finalItems.push(singleLine);
                     }
                    continue;
                }         

                // Single exceptional batch returned, but NOT fully allocated.
                // Use the allocated exceptional qty directly, and top-up the remainder via exceptional dialog (no old/new prompt).
                if (batches.length === 1 && a.fully_allocated === false) {
                    const b = batches[0];
                    const isExceptionalBatch =
                        (typeof b.exceptional !== 'undefined')
                            ? !!b.exceptional
                            : (exInfo?.inventory_id ? b.inventory_id === exInfo.inventory_id : false);

                    if (isExceptionalBatch) {
                        // avoid duplicate (same product + batch) if a line already exists
                        if (isDuplicateItem(pid, b.batch_number, null)) {
                            // if duplicate, just compute remainder and go to exceptional top-up
                            const remainder = Math.max(0, requested - (Number(b.allocated_quantity) || 0));
                            if (remainder > 0) {
                                const itemId = Date.now() + Math.random();
                                finalItems.push({
                                    id: itemId, product: prodObj, quantity: remainder,
                                    price: '', batch_number: null, expiry_date: null,
                                    price_list: null, inventory: null, total: 0,
                                });
                                noStockQ.push({ itemId, productObj: prodObj, productId: pid, qty: remainder, activeExceptional: exInfo });
                                tempExceptionalIdsRef.current.add(itemId);
                            }
                            continue;
                        }
                        // add allocated exceptional line
                        finalItems.push({
                            id: Date.now() + Math.random(),
                            product: prodObj, quantity: b.allocated_quantity, price: b.selling_price,
                            batch_number: b.batch_number, expiry_date: b.expiry_date,
                            price_list: b.price_list_pk, inventory: b.inventory_id,
                            total: (Number(b.allocated_quantity) || 0) * (Number(b.selling_price) || 0),
                        });
                        // add remainder placeholder + queue exceptional top-up
                        const remainder = Math.max(0, requested - (Number(b.allocated_quantity) || 0));
                        if (remainder > 0) {
                            const itemId = Date.now() + Math.random();
                            finalItems.push({
                                id: itemId, product: prodObj, quantity: remainder,
                                price: '', batch_number: null, expiry_date: null,
                                price_list: null, inventory: null, total: 0,
                            });
                            noStockQ.push({ itemId, productObj: prodObj, productId: pid, qty: remainder, activeExceptional: exInfo });
                            tempExceptionalIdsRef.current.add(itemId);
                        }
                        continue;
                    }
                }

                // Multi-batch or partial -> build split rows from server suggestion, then remainder if any
                let allocatedSoFar = 0;
                const splitRows = [];
                for (const b of batches) {
                    if (!b.allocated_quantity) continue;
                    allocatedSoFar += Number(b.allocated_quantity) || 0;
                    splitRows.push({
                        id: Date.now() + Math.random(),
                        product: prodObj,
                        quantity: b.allocated_quantity,
                        price: b.selling_price,
                        batch_number: b.batch_number,
                        expiry_date: b.expiry_date,
                        price_list: b.price_list_pk,
                        inventory: b.inventory_id,
                        total: (Number(b.allocated_quantity) || 0) * (Number(b.selling_price) || 0),
                    });
                }
                const exceptionalAllocated = (batches || []).reduce((s, b) => {
                    const isExc = (typeof b.exceptional !== 'undefined')
                        ? !!b.exceptional
                        : (exInfo?.inventory_id ? b.inventory_id === exInfo.inventory_id : false);
                    return isExc ? s + (Number(b.allocated_quantity) || 0) : s;
                }, 0);
                const remainderExc = Math.max(0, requested - exceptionalAllocated);
                if (exInfo.active) {

                    const allExceptional = batches.length > 0 && batches.every(b => {
                        if (typeof b.exceptional !== 'undefined') return b.exceptional === true;
                        return exInfo?.inventory_id ? b.inventory_id === exInfo.inventory_id : false;
                    });

                    const totalAllocatedAll = batches.reduce(
                        (s, b) => s + (Number(b.allocated_quantity) || 0),
                        0
                    );

                    // CASE 1: ALL exceptional → DO NOT ASK
                    if (allExceptional) {
                        finalItems.push(...splitRows);

                        const remainder = Math.max(0, requested - totalAllocatedAll);

                        if (remainder > 0) {
                            const itemId = Date.now() + Math.random();
                            finalItems.push({
                                id: itemId,
                                product: prodObj,
                                quantity: remainder,
                                price: '',
                                batch_number: null,
                                expiry_date: null,
                                price_list: null,
                                inventory: null,
                                total: 0,
                            });

                            noStockQ.push({
                                itemId,
                                productObj: prodObj,
                                productId: pid,
                                qty: remainder,
                                activeExceptional: exInfo
                            });

                            tempExceptionalIdsRef.current.add(itemId);
                        }

                        continue; 
                    }
                    choiceQueue.push({
                        type: 'multi',
                        pid,
                        prodObj,
                        requested,
                        batches,
                        exceptional: exInfo,
                        hasMore: hasMore,
                        remainder: remainderExc
                    });

                } else {
                    // No exceptional → we can add now
                    finalItems.push(...splitRows);
                    const remainder = Math.max(0, requested - allocatedSoFar);
                    if (remainder > 0) {
                        const itemId = Date.now() + Math.random();
                        finalItems.push({
                            id: itemId,
                            product: prodObj,
                            quantity: remainder,
                            price: '',
                            batch_number: null,
                            expiry_date: null,
                            price_list: null,
                            inventory: null,
                            total: 0,
                        });
                        noStockQ.push({ itemId, productObj: prodObj, productId: pid, qty: remainder, activeExceptional: exInfo });
                        tempExceptionalIdsRef.current.add(itemId);
                    }
                }
            }

            // Write all rows into the invoice at once
            setInvoice(prev => ({
                ...prev,
                prescription: {
                        ...rxDetails,
                        label: `${rxDetails.id} - Dr. ${rxDetails.doctor_name || '(N/A)'} (${new Date(rxDetails.date).toLocaleDateString()})`
                },
                duration: rxDetails.duration,
                discount: null,
                items: finalItems,
            }));

            // Store any excluded-batches (if your bulk API returns them keyed per product, adapt here)
            if (data.excluded_lookup) {
                setExcludedBatchesMap(data.excluded_lookup);
            }

        // If any products had both NEW batches and OLD (exceptional), ask user per product
        if (choiceQueue.length) {
            // process sequentially
            const askNext = (queue) => {
                if (!queue.length) {
                    // When choice queue is done, process no-stock items:
                    if (!allowExceptional) {
                        handleApiSuccess('Prescription items allocated for items with stocks.');
                        return;
                    }
                    if (noStockQ.length) {
                        const withActive    = noStockQ.filter(x => x.activeExceptional?.active);
                        const withoutActive = noStockQ.filter(x => !x.activeExceptional?.active);

                        if (withActive.length) {
                            // Defer withoutActive prompt until withActive exceptional queue is drained
                            if (withoutActive.length) {
                                pendingNoStockWithoutActiveRef.current = withoutActive;
                            }
                            setRxExceptionalQueue(prev => ([...(prev || []), ...withActive]));
                            startRxExceptionalFlow();
                        } else if (withoutActive.length) {
                            // No withActive — safe to prompt immediately
                            const msg = [
                                'Some items are out of stock:',
                                ...withoutActive.map(x => `• ${x.productObj?.name || `#${x.productId}`} (${x.qty})`),
                                '',
                                'Do you have in-hand (old) stocks to add to inventory now?'
                            ].join('\n');
                            confirmWithNav({
                                header: 'No Stock',
                                message: msg,
                                icon: 'pi pi-exclamation-triangle',
                                acceptLabel: 'Add Inventory',
                                rejectLabel: 'Skip',
                                accept: () => {
                                    setRxExceptionalQueue(prev => ([...(prev || []), ...withoutActive]));
                                    startRxExceptionalFlow();
                                },
                                reject: () => handleApiSuccess('Prescription items allocated.'),
                            });
                        } else {
                            // nothing to do
                            handleApiSuccess('Prescription items allocated.');
                        }
                    } else {
                        handleApiSuccess('Prescription items allocated.');
                    }
                    return;
                }

                const current = queue[0];
                const { type, pid, prodObj, requested, singleLine, batches, exceptional, hasMore, remainder } = current;
                const nm = prodObj?.name || `#${prodObj?.id}`;
                confirmWithNav({
                    header: `Choose stock source ${nm}`,
                    message: 'New batches are available, and OLD stocks exist physically.\nWould you like to add to OLD stocks (exceptional) or use the NEW batch allocation?',
                    acceptLabel: 'Add to Old stocks',
                    rejectLabel: 'Use New batch',
                    accept: () => {
                        // Process OLD (exceptional) first: add existing exceptional allocations now,
                        // then top-up only the remainder via exceptional dialog.
                        const isBatchExceptional = (b) => {
                            if (typeof b?.exceptional !== 'undefined') return !!b.exceptional;
                            return exceptional?.inventory_id ? b?.inventory_id === exceptional.inventory_id : false;
                        };

                        if (type === 'multi') {
                            const excBatches = (batches || []).filter(isBatchExceptional);
                            const excTotal  = excBatches.reduce((s, b) => s + (Number(b.allocated_quantity) || 0), 0);

                            // 1) Add the already-allocated exceptional quantities as actual invoice rows.
                            if (excBatches.length) {
                                setInvoice(prev => ({
                                    ...prev,
                                    items: [
                                        ...prev.items,
                                        ...excBatches.map(b => ({
                                            id: Date.now() + Math.random(),
                                            product: prodObj,
                                            quantity: Number(b.allocated_quantity) || 0,
                                            price: b.selling_price,
                                            batch_number: b.batch_number,
                                            expiry_date: b.expiry_date,
                                            price_list: b.price_list_pk,
                                            inventory: b.inventory_id,
                                            total: (Number(b.allocated_quantity) || 0) * (Number(b.selling_price) || 0),
                                        })),
                                    ],
                                }));
                            }

                            // 2) Only top-up the remainder exceptionally (if any).
                            const remainderQty = Math.max(0, Number(requested || 0) - Number(excTotal || 0));
                            if (remainderQty > 0) {
                                // enqueue remainder for exceptional flow and start the runner
                                const itemId = Date.now() + Math.random();
                                setInvoice(prev => ({
                                  ...prev,
                                  items: [
                                    ...prev.items,
                                    {
                                      id: itemId,
                                      product: prodObj,
                                      quantity: remainderQty,
                                      price: '',
                                      batch_number: null,
                                      expiry_date: null,
                                      price_list: null,
                                      inventory: null,
                                      total: 0,
                                    },
                                  ],
                                }));
                                setRxExceptionalQueue(prev =>
                                  ([...(prev || []), { itemId, productObj: prodObj, productId: pid, qty: remainderQty, activeExceptional: exceptional }])
                                );
                                pendingChoiceQueueRef.current = queue.slice(1);
                                resumeAfterExRef.current = true;
                                startRxExceptionalFlow(); 
                                return;
                            } else {
                                // nothing left to top-up → continue the per-product choice queue now
                                const rest = queue.slice(1);
                                if (rest.length) setTimeout(() => choiceAskNextRef.current?.(rest), 0);
                            }
                        } else {
                            // 'single' case: queue the full requested for exceptional flow via the same runner
                            pendingChoiceQueueRef.current = queue.slice(1);
                            const itemId = Date.now() + Math.random();
                            setInvoice(prev => ({
                              ...prev,
                              items: [
                                ...prev.items,
                                {
                                  id: itemId,
                                  product: prodObj,
                                  quantity: requested,
                                  price: '',
                                  batch_number: null,
                                  expiry_date: null,
                                  price_list: null,
                                  inventory: null,
                                  total: 0,
                                },
                              ],
                            }));
                            setRxExceptionalQueue(prev =>
                              ([...(prev || []), { itemId, productObj: prodObj, productId: pid, qty: requested, activeExceptional: exceptional }])
                            );
                            resumeAfterExRef.current = true;
                            startRxExceptionalFlow();
                            return;
                        }
                    },
                    reject: async () => {
                    // Proceed with NEW batches; deactivate the exceptional batch
                    try { await inactivateActiveExceptional(pid); } catch (e) { handleApiError(e) }
                        if (type === 'single') {
                            const newId = Date.now() + Math.random();
                            setInvoice(prev => ({ ...prev, items: [...prev.items, { ...singleLine, id: newId }] }));
                            setManualBatchEnabledFlag(prev => ({ ...prev, [newId]: !!hasMore }));
                        } else {
                            // multi-batch → open split dialog and PAUSE the queue to avoid overlapping modals
                            pendingChoiceQueueRef.current = queue.slice(1);
                            const pendingId = Date.now() + Math.random();
                            setInvoice(prev => ({
                                ...prev,
                                items: [
                                ...prev.items,
                                {
                                    id: pendingId,
                                    product: prodObj,
                                    quantity: requested,
                                    price: '',
                                    batch_number: null,
                                    expiry_date: null,
                                    price_list: null,
                                    inventory: null,
                                    total: 0,
                                },
                                ],
                            }));
                            setPendingItemId(pendingId);
                            setPendingProduct(prodObj);
                            setPendingQty(requested);
                            setBatchAllocations(batches);
                            setPendingExceptional(null);
                            suppressExceptionalAfterSplitRef.current = true;
                            setBatchDialogVisible(true);
                            return;
                        }
                        askNext(queue.slice(1));
                    },
                });
            };
            choiceAskNextRef.current = askNext;
            askNext(choiceQueue);
            return;
        }

            // No choice queue; just handle "no stock" as a single confirm
            if (noStockQ.length) {
                const withActive    = noStockQ.filter(x => x.activeExceptional?.active);
                const withoutActive = noStockQ.filter(x => !x.activeExceptional?.active);

                // 1) Immediately start exceptional flow for items that already have an active exceptional
                if (withActive.length) {
                    // Defer withoutActive prompt until withActive exceptional queue is drained
                    if (withoutActive.length) {
                        pendingNoStockWithoutActiveRef.current = withoutActive;
                    }
                    setRxExceptionalQueue(withActive);
                    startRxExceptionalFlow();
                } else if (withoutActive.length) {
                    // No withActive — safe to prompt immediately
                    if (!allowExceptional) {
                        handleApiSuccess('Prescription items allocated for items with stocks.')
                        return;
                    }
                    const msg = [
                    'Some items are out of stock:',
                    ...withoutActive.map(x => `• ${x.productObj?.name || `#${x.productId}`} (${x.qty})`),
                    '',
                    'Do you have in-hand (old) stocks to add to inventory now?'
                    ].join('\n');

                    confirmWithNav({
                    header: 'No Stock',
                    message: msg,
                    icon: 'pi pi-exclamation-triangle',
                    acceptLabel: 'Add Inventory',
                    rejectLabel: 'Skip',
                    accept: () => {
                        setRxExceptionalQueue(withoutActive);
                        startRxExceptionalFlow();
                    },
                    reject: () => handleApiSuccess('Prescription items allocated.'),
                    });
                } else {
                    // Nothing to do
                    handleApiSuccess('Prescription items allocated.');
                }
                return;
            }
        } catch (err) {
            handleApiError(err);
        }
    };

    // Called by ExceptionalInventoryDialog after a successful POST
    const handleExceptionalSubmitSuccess = (form, r) => {
        const f = form;
        setInvoice(prev => {
            const idx = prev.items.findIndex(i => i.id === f.itemId);
            if (idx === -1) return prev;
            const original = prev.items[idx];
            const price = r.selling_price ?? f.selling_price ?? (original.price ?? 0);
            const targetBatch = r.batch_number || f.batch_number || original.batch_number;
            const items = [...prev.items];
            const normRowPid = (p) => p && typeof p === 'object' ? String(p.id ?? '') : String(p ?? '');
            const productIdStr = String(f.productObj?.id ?? f.productId ?? (original.product?.id ?? original.product));
            const normBatch = (b) => String(b ?? '').trim().toUpperCase();
            const addQty = Number(f.qty) || 0;
            const targetBatchNorm = normBatch(targetBatch);
            const origPidStr    = normRowPid(original.product);
            const origBatchNorm = normBatch(original.batch_number);
            if (origPidStr === productIdStr && origBatchNorm === targetBatchNorm) {
                const newQty = (Number(original.quantity) || 0) + addQty;
                items[idx] = { ...original, quantity: newQty, total: newQty * (Number(original.price ?? price) || 0), __qtyKey: (original.__qtyKey || 0) + 1 };
                return { ...prev, items };
            }
            const mergeIdx = items.findIndex(i =>
                i.id !== f.itemId &&
                normRowPid(i.product) === String(productIdStr) &&
                normBatch(i.batch_number) === targetBatchNorm
            );
            if (mergeIdx >= 0) {
                const merged = { ...items[mergeIdx] };
                merged.quantity = (Number(merged.quantity) || 0) + addQty;
                merged.total = Number(merged.quantity) * (Number(merged.price ?? price) || 0);
                items[mergeIdx] = merged;
                if (!!original.parent_item_id || !original.batch_number) items.splice(idx, 1);
                return { ...prev, items };
            }
            const newItem = {
                ...original,
                id: Date.now() + Math.random(),
                product: f.productObj,
                quantity: Number(r.quantity),
                price,
                batch_number: targetBatch,
                expiry_date: r.expiry_date || (f.expiry ? format(f.expiry, 'yyyy-MM-dd') : null),
                price_list: r.price_list_pk || original.price_list || null,
                inventory:  r.inventory_id  || original.inventory  || null,
                total: Number(f.qty) * Number(price),
            };
            if (!!original.parent_item_id || !original.batch_number) items.splice(idx, 1, newItem);
            else items.splice(idx + 1, 0, newItem);
            return { ...prev, items };
        });
        setManualBatchMap(prev => ({ ...prev, [f.itemId]: false }));
        setBatchOptionsMap(prev => ({ ...prev, [f.itemId]: [] }));
        setExceptionalVisible(false);
        setRxExceptionalQueue(prev => {
            const rest = (prev || []).slice(1);
            if (rest.length) {
                const next = rest[0];
                setTimeout(() => openExceptionalDialog(
                    next.activeExceptional?.active ? 'existing' : 'new',
                    { itemId: next.itemId, productObj: next.productObj, productId: next.productId, qty: next.qty },
                    next.activeExceptional?.active ? next.activeExceptional : null
                ), 0);
            } else {
                const nextChoices = pendingChoiceQueueRef.current || [];
                pendingChoiceQueueRef.current = [];
                if (nextChoices.length) {
                    setTimeout(() => choiceAskNextRef.current?.(nextChoices), 0);
                } else {
                    // Resume deferred no-stock prompt for withoutActive items, if any
                    const deferred = pendingNoStockWithoutActiveRef.current || [];
                    pendingNoStockWithoutActiveRef.current = [];
                    if (deferred.length) {
                        const msg = [
                            'Some items are out of stock:',
                            ...deferred.map(x => `• ${x.productObj?.name || `#${x.productId}`} (${x.qty})`),
                            '',
                            'Do you have in-hand (old) stocks to add to inventory now?'
                        ].join('\n');
                        confirmWithNav({
                            header: 'No Stock',
                            message: msg,
                            icon: 'pi pi-exclamation-triangle',
                            acceptLabel: 'Add Inventory',
                            rejectLabel: 'Skip',
                            accept: () => {
                                setRxExceptionalQueue(deferred);
                                startRxExceptionalFlow();
                            },
                            reject: () => handleApiSuccess('Prescription items allocated.'),
                        });
                    }
                }
            }
            return rest;
        });
    };

    const handleExceptionalCancel = (itemId) => {
        setExceptionalVisible(false);
        if (itemId && tempExceptionalIdsRef.current.has(itemId)) {
            setInvoice(prev => ({ ...prev, items: prev.items.filter(it => it.id !== itemId) }));
            setManualBatchMap(prev => { const n = { ...prev }; delete n[itemId]; return n; });
            setBatchOptionsMap(prev => { const n = { ...prev }; delete n[itemId]; return n; });
            setExcludedBatchesMap(prev => { const n = { ...prev }; delete n[itemId]; return n; });
            tempExceptionalIdsRef.current.delete(itemId);
        }
        setRxExceptionalQueue(prev => {
            const rest = (prev || []).slice(1);
            if (rest.length) {
                const next = rest[0];
                setTimeout(() => openExceptionalDialog(
                    next.activeExceptional?.active ? 'existing' : 'new',
                    { itemId: next.itemId, productObj: next.productObj, productId: next.productId, qty: next.qty },
                    next.activeExceptional?.active ? next.activeExceptional : null
                ), 0);
            } else {
                const nextChoices = pendingChoiceQueueRef.current || [];
                pendingChoiceQueueRef.current = [];
                if (nextChoices.length) {
                    setTimeout(() => choiceAskNextRef.current?.(nextChoices), 0);
                } else {
                    // Resume deferred no-stock prompt for withoutActive items, if any
                    const deferred = pendingNoStockWithoutActiveRef.current || [];
                    pendingNoStockWithoutActiveRef.current = [];
                    if (deferred.length) {
                        const msg = [
                            'Some items are out of stock:',
                            ...deferred.map(x => `• ${x.productObj?.name || `#${x.productId}`} (${x.qty})`),
                            '',
                            'Do you have in-hand (old) stocks to add to inventory now?'
                        ].join('\n');
                        confirmWithNav({
                            header: 'No Stock',
                            message: msg,
                            icon: 'pi pi-exclamation-triangle',
                            acceptLabel: 'Add Inventory',
                            rejectLabel: 'Skip',
                            accept: () => {
                                setRxExceptionalQueue(deferred);
                                startRxExceptionalFlow();
                            },
                            reject: () => handleApiSuccess('Prescription items allocated.'),
                        });
                    }
                }
            }
            return rest;
        });
    };
    const handleBatchCancel = () => {
        setBatchDialogVisible(false);
        // Resume the per-product choice queue that we paused before opening split
        if (choiceAskNextRef.current) {
            const rest = (pendingChoiceQueueRef.current || []).slice(0);
            pendingChoiceQueueRef.current = [];
            if (rest.length) {
                setTimeout(() => choiceAskNextRef.current(rest), 0);
            }
        }
    };
    const columns = useMemo(() => [
        { 
            Header: 'Invoice #', 
            accessor: 'invoice_number', 
            bodyTemplate: (rowData) => (
            <a
                href="/invoices/#"
                style={{ textDecoration: 'none' }}
                onClick={(e) => {
                    beginDrilldown();
                    e.preventDefault();
                    handleRowClick(rowData);
                    push({
                        label: `Inv: ${rowData.invoice_number}`,
                    });
                }}
            >
                {rowData.invoice_number}
            </a>
        ),},
        { Header: 'Customer', accessor: 'customer_name', sortable: true,
            bodyTemplate: (row) => (
            <span
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={() =>{
                    beginDrilldown();
                    navigate(`/customers/${row.customer}`);
                    push({
                        label: `Invoices`,
                        path: '/invoices',
                    });
                    push({
                        label: `Customer: ${row.customer_name}`,
                        path: `/customers/${row.customer}`,
                    });
                }}
            >
                {row.customer_name}
            </span>
            )
         },
        { Header: 'Department', accessor: 'department_name', sortable: true, filtered: true },
        { Header: 'Prescription', accessor:'prescription',
            bodyTemplate: (row) => (
            <span
                style={{ color: '#2563eb', cursor: 'pointer' }}
                onClick={() =>{
                    beginDrilldown();
                    navigate('/prescriptions', {
                        state: {prescriptionId: row.prescription}
                    });
                    push({
                        label: `Invoices`,
                        path: '/invoices',
                    });
                    push({
                        label: `Prescriptions`,
                        path: '/prescriptions',
                    });
                    push({
                        label: `Prescription: ${row.prescription}`,
                        path: '/prescriptions',
                        state: {prescriptionId: row.prescription}
                    });
                }}
            >
                {row.prescription}
            </span>
            )
        },
        { Header: 'Date', accessor: 'date', sortable: true, filtered: true, dataType: 'date', matchMode:FilterMatchMode.DATE_IS, filterElement:dateFilterTemplate },
        { Header: 'Total Price', accessor: 'total_price', sortable: true, filtered: true, dataType:"numeric", matchMode:FilterMatchMode.EQUALS, filterElement:currencyFilterTemplate },
        { Header: 'Net Price', accessor: 'net_price', sortable: true,  filtered:true, dataType:"numeric", matchMode:FilterMatchMode.EQUALS, filterElement:currencyFilterTemplate },
        { Header: 'Status', accessor: 'status', sortable: true },
    ], [handleRowClick, beginDrilldown, navigate, push, dateFilterTemplate, currencyFilterTemplate]);

    const renderHeader = () => (
    <div className="flex justify-content-between align-items-center gap-4 md:gap-2" style={{ marginRight: '15px' }}>
        <h5 className="text-base sm:text-lg lg:text-xl" style={{ margin: 0 }}>Invoice Items:</h5>

        {/* Scan input - visible, accessible, placeholder as requested */}
        <div style={{ flex: 1, maxWidth: '420px' }}>
            <InputText
            ref={scanInputRef}
            placeholder="Scan barcode to enter item"
            value={undefined} // uncontrolled; scanner will write into it — keep React from managing value
            onKeyDown={async (e) => {
                // If scanner sends Enter, treat as complete
                if (e.key === 'Enter') {
                e.preventDefault();
                const val = e.target.value?.trim();
                e.target.value = '';
                if (val) await handleScannedCode(val);
                }
            }}
            onBlur={(e) => {
                // Clear stray characters on blur
                if (e.target) e.target.value = '';
            }}
            className="w-full"
            disabled={isReadOnly || !canCreateInvoices}
            />
        </div>

        <div className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ marginLeft: '10px' }}>
            <Button type="button" icon="pi pi-plus" onClick={(e) => { e.preventDefault(); addNewItem(); }} disabled={isReadOnly || !canCreateInvoices} className="p-mt-2" style={{ borderRadius: "6px" }} />
            <Button icon={<FaFlag color="red" />} disabled={!selectedItem || !canCreateInvoices || !selectedItem.product.id} className="p-button-text p-button-sm" onClick={(e) => { e.preventDefault(); handleOpenTagIssue(selectedItem.product) }} title="Tag Issue" style={{ marginLeft: "10px" }} />
        </div>
    </div>
    );

    const header = renderHeader();
    
    const renderPresHeader = () => (
            <div className="flex justify-content-between align-items-center gap-4 md:gap-2" style={{marginRight:'50px'}}>
                <h5 className="text-base sm:text-lg lg:text-xl">Prescription Items:</h5>
                    <div className="flex flex-wrap align-items-center gap-2 sm:gap-2">
                        <Button type="button" icon="pi pi-plus" onClick={(e) => {e.preventDefault(); addNewPresItem(); }} className="p-mt-2" style={{borderRadius:"6px"}} />
                    </div>
            </div>
        );
    const presHeader = renderPresHeader();

    const cancelHandler = (e) => {
        setPrescriptionDialogVisible(false);
        e.preventDefault(); 
        setPrescription({
            customer: null,
            doctor_name: '',
            duration: null,
            date: new Date(),
            items: [{ product: null, quantity: '', dosage: '1', frequency: '', duration: '' }],
        });
    }

    const headerAction = canCreateInvoices ? (
        <Button
            type="button"
            icon="pi pi-plus"
            label="Add Invoice"
            outlined
            onClick={() => {
                setDialogName('Create Invoice')
                setDialogVisible(true);
                setInvoiceOperator(null);
                if (operatorAuthEnabled && isNewInvoice) {
                    setInvoiceOperator(null);
                    setShowOperatorAuth(true);
                }
                setInvoice({
                    customer: null,
                    invoiced_customer: '',
                    date: new Date(),
                    prescription: null,
                    remarks: '',
                    total_price: 0,
                    discount: null,
                    tax_amount: 0,
                    net_price: 0, 
                    isdiscount_per: true,
                    items: [makeBlankItem(null)],
                })
                setIsReadOnly(false);
                setOrgSettings();
            }}
            />
    ) : null;

    return (
        <UploadFilePanel uploadData={invoiceUploadData}>
        <div style={{ position: 'relative', minHeight: '100%' }}>
            <div className="containers">
            <GlobalTable
                columns={columns}
                data={invoices}
                headerAction={headerAction}
                tabletitle="Invoice List"
                loading={loading}
                showEditColumn={false}
                lazy
                totalRecords={totalRecords}
                lazyParams={lazyParams}
                onPage={onPage}
                onSort={onSort}
                onFilter={onFilter}
            />
            <CustomerDialog
                visible={isCustomerDialogVisible && canManageCustomers}
                onHide={() => setCustomerDialogVisible(false)}
                customer={customer}
                handleChange={handleCusChange}
                handleSubmit={handleCusSubmit}
                isReadOnly={false}
                dialogName={'Create Customer'}
                isRequired={true}
                titles={titles}
                categories={categories}
                segments={segments}
                priority={priority}
                status={status}
            />
            </div>
            <BatchAllocationDialog
                visible={batchDialogVisible}
                onHide={handleBatchCancel}
                allocations={batchAllocations}
                onAllocationChange={handleBatchAllocationChange}
                onConfirm={handleBatchConfirm}
                totalQty={pendingQty}
            />

            <CreateInvoiceDialog
                visible={dialogVisible}
                onHide={() => { setDialogVisible(false); setInvoiceOperator(null); setShowOperatorAuth(false); setIsCreditNote(false); setInvoiceUploadData(null); pop(); }}
                fromUpload={!!fromUploadId}
                invoice={invoice}
                dialogName={dialogName}
                isReadOnly={isReadOnly}
                currentTime={<LiveClock />}
                invoiceFormRef={invoiceFormRef}
                isCompletingRef={isCompletingRef}
                rowBumps={rowBumps}
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                manualBatchEnabledFlag={manualBatchEnabledFlag}
                manualBatchMap={manualBatchMap}
                setManualBatchMap={setManualBatchMap}
                batchOptionsMap={batchOptionsMap}
                setBatchOptionsMap={setBatchOptionsMap}
                excludedBatchesMap={excludedBatchesMap}
                filteredCustomers={filteredCustomers}
                filteredPrescriptions={filteredPrescriptions}
                cusinputRef={cusinputRef}
                priceEditable={priceEditable}
                discountApproval={discountApproval}
                discountEditable={discountEditable}
                overrideApproval={overrideApproval}
                showOperatorAuth={showOperatorAuth}
                operatorAuthKey={operatorAuthKey}
                invoiceOperator={invoiceOperator}
                isCreditNote={isCreditNote}
                itemsTableHeader={header}
                prodInputRefsInv={prodInputRefsInv}
                handleSearchCustomer={handleSearchCustomer}
                handlePrescriptionChange={handlePrescriptionChange}
                handleDurationChange={handleDurationChange}
                handleItemChange={handleItemChange}
                handleSubmit={handleSubmit}
                handleDrop={handleDrop}
                handlePrint={handlePrint}
                printing={printing}
                confirmCreditNote={confirmCreditNote}
                openPaymentDialog={openPaymentDialog}
                removeItem={removeItem}
                fetchBatchAllocations={fetchBatchAllocations}
                showExcludedDialog={showExcludedDialog}
                calculatePrices={calculatePrices}
                setPasswordPurpose={setPasswordPurpose}
                setPasswordDialogHeader={setPasswordDialogHeader}
                setPasswordDialogVisible={setPasswordDialogVisible}
                setInvoice={setInvoice}
                setPrescription={setPrescription}
                setPrescriptionDialogVisible={setPrescriptionDialogVisible}
                setCustomerDialogVisible={setCustomerDialogVisible}
                setIsCompleting={setIsCompleting}
                setDialogVisible={setDialogVisible}
                setInvoiceOperator={setInvoiceOperator}
                setShowOperatorAuth={setShowOperatorAuth}
                setIsCreditNote={setIsCreditNote}
                setIsReadOnly={setIsReadOnly}
                setOrgSettings={setOrgSettings}
                setInventoryError={setInventoryError}
                setInventoryInfo={setInventoryInfo}
                setshowCreditNoteDialog={setshowCreditNoteDialog}
                makeBlankItem={makeBlankItem}
                applyCustomerLoyaltyDiscount={applyCustomerLoyaltyDiscount}
                handleSearchPrescriptions={handleSearchPrescriptions}
                pop={pop}
                onCancelFromUpload={() => {
                    setDialogVisible(false);
                    setInvoiceOperator(null);
                    setShowOperatorAuth(false);
                    setIsCreditNote(false);
                    setIsReadOnly(false);
                    setOrgSettings();
                    setInventoryError('');
                    setInventoryInfo('');
                    setInvoice({ customer: null, invoiced_customer: '', date: new Date(), prescription: null, remarks: '', total_price: 0, discount: null, tax_amount: 0, net_price: 0, isdiscount_per: true, items: [makeBlankItem(null)] });
                    pop();
                    navigate(location.pathname, { replace: true, state: {} });
                }}
                onOperatorVerified={(data) => {
                    setInvoiceOperator(data);
                    setShowOperatorAuth(false);
                    if (isCreditNote) { setshowCreditNoteDialog(true); setIsCreditNote(false); }
                }}
                onOperatorCancel={() => {
                    setShowOperatorAuth(false);
                    setDialogVisible(false);
                    setInvoiceOperator(null);
                    setIsCreditNote(false);
                }}
                axiosInstance={axiosInstance}
                handleApiError={handleApiError}
                canCreateInvoices={canCreateInvoices}           
                canManageCustomers={canManageCustomers}          
                canManagePrescriptions={canManagePrescriptions} 
                canManageCreditNotes={canManageCreditNotes}     
                canViewPayments={canViewPayments}   
                showMarkReady={showMarkReady}
                markReadyAlreadySent={markReadyAlreadySent}
                onMarkReady={handleMarkReady}
                portalUploadData={invoiceUploadData}
                markReadyLoading={markReadyLoading}
            />
        
        <Dialog
            header={passwordDialogHeader}
            visible={passwordDialogVisible}
            onHide={() => setPasswordDialogVisible(false)}
            modal
            style={{ width: '30rem', borderRadius: '12px' }}
        >
            <form onSubmit={handlePasswordSubmit}>
                <div className="field">
                    <label htmlFor="password" className="font-bold">Password:</label>
                    <Password
                        id="password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        required
                        feedback={false}
                        placeholder="Enter password"
                        style={{ borderRadius: '6px' }}
                    />
                </div>
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem' }}>
                    <Button label="Submit" icon="pi pi-check" type="submit" style={{ borderRadius: '6px' }} />
                    <Button
                        label="Cancel"
                        icon="pi pi-times"
                        outlined
                        onClick={(e) => {e.preventDefault(); setPasswordDialogVisible(false); setPassword(''); setExcludedBatchesMap({});}}
                        style={{ borderRadius: '6px' }}
                    />
                </div>
            </form>
        </Dialog>
        <PrescriptionDialog
                visible={prescriptionDialogVisible && canManagePrescriptions}
                onHide={() => setPrescriptionDialogVisible(false)}
                prescription={prescription}
                handleItemChange={handlePresItemChange}
                handleSubmit={handlePresSubmit}
                handleSearchCustomer={handleSearchCustomer}
                handleDurationChange={handlePresDurationChange}
                handleRevise={handleRevise}
                setPrescription={setPrescription}
                removeItem={removePresItem}
                isReadOnly={false}
                dialogName={'Create Prescription'}
                filteredCustomers={filteredCustomers}
                header={presHeader}
                frequency={frequency}
                modulehandler={'INV'}
                cancelhandler={cancelHandler}
                prodInputRefs={prodInputRefs}
                addNewItem={addNewPresItem}
            />
             {/* Credit Note Dialog */}
             <Dialog 
                visible={showCreditNoteDialog} 
                onHide={() => setshowCreditNoteDialog(false)} 
                header="Issue Credit Note" 
                modal
                footer={
                    <>
                        <div  className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px' }}>
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setshowCreditNoteDialog(false)} severity="secondary" style={{borderRadius:"6px"}}/>
                        <Button label="Refund to new Invoice" icon="pi pi-check" onClick={() => issueCreditNote(invoice.id)} severity="success" style={{borderRadius:"6px"}}/>
                        </div>
                    </>
                }
            >
                <p>Please provide a reason for issuing this credit note:</p>
                <InputTextarea 
                    rows={3} 
                    className="w-full mt-2"
                    value={creditNoteReason}
                    onChange={(e) => setCreditNoteReason(e.target.value)}
                    placeholder="Enter reason here..."
                />
            </Dialog>
            <Dialog header="Tag Product Issue" visible={productIssueDialog} onHide={() => setProductIssueDialog(false)} style={{borderRadius:"6px", width:"400px"}}
                 footer={
                    <>
                        <div  className="flex flex-wrap align-items-center gap-2 sm:gap-2" style={{ margin: '10px', justifyContent: 'flex-end' }}>
                        <Button label="Tag" icon="pi pi-check" onClick={handleSubmitProductIssue} style={{borderRadius:"6px"}} />
                        <Button label="Cancel" icon="pi pi-times" onClick={() => setProductIssueDialog(false)} severity="secondary" style={{borderRadius:"6px"}}/>
                        </div>
                    </>
                }>
                <p><strong>Product:</strong> {selectedItem?.product?.name}</p>
                <InputTextarea
                    value={issueDescription}
                    onChange={(e) => setIssueDescription(e.target.value)}
                    rows={3}
                    cols={30}
                    placeholder="Describe the issue"
                />
                <div style={{ marginTop: "10px" }}>
                    
                </div>
            </Dialog>
            <Dialog visible={showPrint} onHide={() => setShowPrint(false)} >
                <InvoicePrintView
                    invoiceData={invoice}
                    onClose={() => setShowPrint(false)}
                    showPrint={showPrint}
                />
            </Dialog>
            <Dialog
                header="Excluded (Blocked) Batches"
                visible={excludedDialogVisible}
                onHide={() => setExcludedDialogVisible(false)}
                style={{ width: '30rem' }}
                >
                {currentExcludedList.length === 0
                    ? <p>No details available.</p>
                    : (
                    <ul>
                        {currentExcludedList.map((b, i) => (
                        <li key={i}>
                            Batch <strong>{b.batch_number}</strong> – Expires:{' '}
                            {new Date(b.expiry_date).toLocaleDateString()}
                        </li>
                        ))}
                    </ul>
                    )}
            </Dialog>
            <InlineBatchEditorDialog
                visible={batchAllocDialogVisible}
                items={batchAllocItems}
                availableItems={availableItems}
                onHide={() => setBatchAllocDialogVisible(false)}
                onConfirm={(confirmedItems) => {
                    setBatchAllocDialogVisible(false);
                    postAllocCallbackRef.current?.(confirmedItems);
                }}
            />
            <PaymentDialog
                visible={showPaymentDialog}
                onHide={() => setShowPaymentDialog(false)}
                invoice={invoice}
                invoiceOperator={invoiceOperator}
                onPaymentSaved={handlePaymentSaved}
            />
            <ExceptionalInventoryDialog
                ref={exceptionalDialogRef}
                visible={exceptionalVisible}
                onHide={(itemId) => handleExceptionalCancel(itemId)}
                exceptionalActiveInfo={exceptionalActiveInfo}
                onSubmitSuccess={handleExceptionalSubmitSuccess}
                confirmWithNav={confirmWithNav}
            />
            <Dialog
                header="Scanned Batch — Enter Quantity"
                visible={scannedQtyDialogVisible}
                modal
                onHide={cancelScannedQty}
                style={{ width: '26rem', borderRadius: '8px' }}
                onShow={() => {
                    // focus the numeric input quickly after dialog appears
                    setTimeout(() => {
                    scannedQtyRef.current?.focus?.();
                    try { scannedQtyRef.current?.select?.(); } catch (_) {}
                    }, 40);
                }}
                >
                <div className="p-fluid">
                    <div className="p-field">
                    <label className="font-bold">Product</label>
                    <div>{scannedNewRow?.product?.name || scannedNewRow?.product?.label}</div>
                    </div>

                    <div className="p-field">
                    <label className="font-bold">Batch</label>
                    <div>{scannedNewRow?.batch_number}</div>
                    </div>

                    <div className="p-field">
                    <label className="font-bold">Available Quantity</label>
                    <div>{scannedAvailableQty}</div>
                    </div>

                    <div className="p-field">
                    <label className="font-bold">Quantity to add</label>
                    <InputNumber
                        inputRef={scannedQtyRef}
                        value={scannedQtyValue}
                        onValueChange={(e) => setScannedQtyValue(e.value ?? 0)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                e.preventDefault();
                                // Force-read the DOM input value immediately so confirmScannedQty sees it.
                                try {
                                    const dom = scannedQtyRef?.current;
                                    if (dom) {
                                        // dom.value is the typed string
                                        const domVal = dom.value;
                                        const parsed = domVal === '' ? null : Number(domVal);
                                        // update local state synchronously so confirmScannedQty picks it
                                        setScannedQtyValue(parsed);
                                    }
                                } catch (err) {
                                    // ignore
                                }
                                // small micro-delay to ensure state update (if any) is applied, then confirm
                                setTimeout(() => confirmScannedQty(), 0);
                            } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelScannedQty();
                            }
                        }}
                        min={0}
                        max={scannedAvailableQty || undefined}
                        className="w-full"
                        placeholder="Enter qty"
                    />
                    </div>
                    <div className="flex justify-content-end gap-2 mt-3">
                    <Button label="Cancel" className="p-button-text" onClick={cancelScannedQty} />
                    <Button label="Add" icon="pi pi-check" onClick={confirmScannedQty} />
                    </div>
                </div>
                </Dialog>
                <PrinterModeConfirmDialog
                    visible={printerModeDialog.visible}
                    printerName={printerModeDialog.printerName}
                    requiredLanguage={printerModeDialog.requiredLanguage}
                    onConfirm={printerModeDialog.onConfirm}
                    onCancel={() => setPrinterModeDialog({ visible: false })}
                />
        </div>
        </UploadFilePanel>
    );
};

export default InvoiceList;